import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getDraftRecipe, createCookingSession, completeCookingSession, getCookingSessions, getKernelById, pauseCookingSession, resumeCookingSession, getRecipeById, createCookingSessionFromRecipe, deleteCookingSession, getCookingSessionById, updateRecipe, uploadImage } from '../services/api';
import { Section, Heading, Text, PopupWindow, Modal } from '../components/ui';
import TimerIcon from '../components/ui/TimerIcon';
// VoiceConsentModal removed - voice preference now handled by MicrophonePermissionModal before session starts
import FloatingChef from '../components/FloatingChef';
import FloatingIngredientsTab from '../components/FloatingIngredientsTab';
import FloatingAlarmsTab from '../components/FloatingAlarmsTab';
import voiceService from '../services/voiceService';
import voiceCommandProcessor from '../services/voiceCommandProcessor';
import liveSessionEvents from '../services/liveSessionEvents';
import LiveSessionEventDebugger from '../components/LiveSessionEventDebugger';
import chefReminders from '../services/chefReminders';
import sessionReport from '../services/sessionReport';
import photoCapture from '../services/photoCapture';
import UnifiedChefReminderModal from '../components/UnifiedChefReminderModal';
import ChefRemindersDebugger from '../components/ChefRemindersDebugger';
import SessionResumeModal from '../components/SessionResumeModal';
import PausedSessionModal from '../components/PausedSessionModal';
import ChefSummariesModal from '../components/ChefSummariesModal';
import { chefSummaries } from '../services/chefSummaries';
import AiTipPopup from '../components/AiTipPopup';
import aiTipsService from '../services/aiTipsService';
import PostingModal from '../components/PostingModal';
import ReflectionModal from '../components/ReflectionModal';
import TimerManagementPopup from '../components/TimerManagementPopup';
import { logEvent, logError, logDebug } from '../utils/logger';

const VOICE_RESTART_DELAY_MS = 400;
const VOICE_SILENCE_TIMEOUT_MS = 8000;
const MIN_GAP_AFTER_SPEECH_MS = 1500;

// Ingredients Popup Window Component
const IngredientsPopupWindow = ({ title, ingredients = [], checkedIngredients, onToggleIngredient, onClose, style = {} }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(() => {
        const top = parseInt(style.top) || 96;
        const left = parseInt(style.left) || 96;
        return { x: left, y: top };
    });

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('.title-bar')) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    }, [position]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    return (
        <div 
            className="fixed z-50 bg-neutral-0 w-96 shadow-popup border border-black"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Title Bar */}
            <div className="title-bar bg-neutral-0 text-neutral-900 px-4 py-2 flex justify-between items-center cursor-grab active:cursor-grabbing select-none hairline-b">
                <span className="font-mono uppercase text-sm tracking-wider truncate">
                    {title}
                </span>
                <button 
                    onClick={onClose}
                    className="text-neutral-900 text-base flex-shrink-0 border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:text-neutral-500 transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Content Area - Ingredients Checklist */}
            <div className="max-h-96 overflow-y-auto p-4">
                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-4">
                    Check off ingredients as you gather them
                </Text>
                
                <div className="space-y-2">
                    {ingredients.map((ingredient, index) => {
                        const ingredientKey = `ingredient_${index}`;
                        const isChecked = checkedIngredients.has(ingredientKey);
                        
                        return (
                            <div 
                                key={index}
                                className={`flex items-start gap-3 p-3 cursor-pointer transition-all border ${
                                    isChecked 
                                        ? 'bg-neutral-900 text-neutral-0 border-neutral-900' 
                                        : 'bg-neutral-0 text-neutral-900 border-neutral-900 hover:bg-neutral-50'
                                }`}
                                onClick={() => onToggleIngredient && onToggleIngredient(ingredientKey)}
                            >
                                <div className="flex-shrink-0 mt-0.5">
                                    <div className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                                        isChecked 
                                            ? 'border-neutral-0 bg-neutral-0' 
                                            : 'border-neutral-900 bg-neutral-0'
                                    }`}>
                                        {isChecked && (
                                            <span className="text-neutral-900 text-sm font-bold">✓</span>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                    <Text className={`text-sm font-medium ${
                                        isChecked ? 'text-white' : 'text-neutral-900'
                                    }`}>
                                        {ingredient}
                                    </Text>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {ingredients.length === 0 ? (
                    <div className="text-center py-8 border-t border-neutral-900 mt-4">
                        <Text className="text-neutral-900 opacity-60 font-mono text-sm uppercase tracking-wider">
                            No ingredients available for this recipe
                        </Text>
                    </div>
                ) : (
                    <div className="mt-4 pt-4 border-t border-neutral-900">
                        <Text className="text-neutral-900 opacity-60 text-sm text-center font-mono uppercase tracking-wider">
                            {checkedIngredients.size} of {ingredients.length} ingredients gathered
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

const LiveSession = () => {
    const { draftId, recipeId } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    
    // Determine if we're working with a draft or recipe
    const isRecipe = Boolean(recipeId);
    const sourceId = isRecipe ? recipeId : draftId;
    
    const [draft, setDraft] = useState(null);
    const [recipe, setRecipe] = useState(null);
    const [kernel, setKernel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    
    // Session state
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    // Multi-timer state - array of active timers
    const [activeTimers, setActiveTimers] = useState([]); // Array of {id, title, duration, remaining, isActive, isMuted, stepIndex?, kind?}
    const timerIdCounter = useRef(0);
    
    // Legacy single timer state (for backward compatibility during migration)
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [narrateEnabled, setNarrateEnabled] = useState(false);
    const [sessionComplete, setSessionComplete] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [showPrerequisites, setShowPrerequisites] = useState(false); // Initialize to false, will be set based on actual prerequisites
    const [timerAlarmActive, setTimerAlarmActive] = useState(false); // Track if timer alarm is currently playing
    const [showTimerPopup, setShowTimerPopup] = useState(false); // Track if timer management popup is open
    const [expandedSteps, setExpandedSteps] = useState(new Set()); // Track which upcoming steps have their text expanded
    
    // User preferences state
    const [userPreferences, setUserPreferences] = useState({
        alarm_sound: 'default',
        alarm_enabled: true,
        custom_alarm_url: null,
        auto_start_timers: true, // Always true
        show_ingredients_popup: true, // Always true
        narration_enabled: false,
        alarm_volume: 0.5 // Add volume control (0.0 to 1.0)
    });
    
    // Session time tracking
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [totalElapsedTime, setTotalElapsedTime] = useState(0); // in seconds
    const [lastResumeTime, setLastResumeTime] = useState(null);
    
    // Kernel slideshow state
    const [showKernelSlideshow, setShowKernelSlideshow] = useState(false);
    
    // Ingredients popup state
    const [showIngredientsPopup, setShowIngredientsPopup] = useState(true);
    const [checkedIngredients, setCheckedIngredients] = useState(new Set());
    
    // Pause/Resume state
    const [isPaused, setIsPaused] = useState(false);
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    
    // Voice companion state
    const [showVoiceConsent, setShowVoiceConsent] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [voiceListening, setVoiceListening] = useState(false);
    const [voiceTriggered, setVoiceTriggered] = useState(false);
    
    // Agent settings state
    const [agentSettings, setAgentSettings] = useState({
        agent_name: 'Chef',
        agent_trigger: 'Hey Chef',
        agent_enabled: true,
        agent_preferences: {}
    });
    const [lastVoiceCommand, setLastVoiceCommand] = useState('');
    const [voiceConsentGiven, setVoiceConsentGiven] = useState(false);
    const [lastCommandTime, setLastCommandTime] = useState(0);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [lastSpeechEndTime, setLastSpeechEndTime] = useState(Date.now());
    const [isAIThinking, setIsAIThinking] = useState(false);
    const [isWaitingForQuestion, setIsWaitingForQuestion] = useState(false);
    const [voiceDisabled, setVoiceDisabled] = useState(false);
    const [agentSettingsLoaded, setAgentSettingsLoaded] = useState(false);
    
    // Chef Reminders state
    const [chefRemindersEnabled, setChefRemindersEnabled] = useState(false);
    const [currentReminder, setCurrentReminder] = useState(null);
    const [showReminderModal, setShowReminderModal] = useState(false);
    
    // Chef Summaries state
    const [chefSummariesEnabled, setChefSummariesEnabled] = useState(false);
    const [showChefSummariesModal, setShowChefSummariesModal] = useState(false);
    const [chefSummariesReport, setChefSummariesReport] = useState(null);
    
    // AI Tips state (controlled by chef_recommendations setting)
    const [aiTipsEnabled, setAiTipsEnabled] = useState(false);
    const [aiTips, setAiTips] = useState([]);
    const [currentTip, setCurrentTip] = useState(null);
    const [showTipPopup, setShowTipPopup] = useState(false);
    const [tipsGenerated, setTipsGenerated] = useState(false);
    
    // Posting Modal state
    const [showPostingModal, setShowPostingModal] = useState(false);
    
    // Reflection Modal state
    const [showReflectionModal, setShowReflectionModal] = useState(false);
    
    // Session Resume state
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [showPausedModal, setShowPausedModal] = useState(false);
    const [pausedSession, setPausedSession] = useState(null);
    
    // Chef position state (synced with localStorage)
    const [chefPosition, setChefPosition] = useState(() => {
        const saved = localStorage.getItem('chefTabPosition');
        return saved ? JSON.parse(saved) : { edge: 'bottom', offset: 24 };
    });
    
    // Always-on state (prevents computer sleep)
    const [alwaysOnEnabled, setAlwaysOnEnabled] = useState(false);
    const [wakeLockSupported, setWakeLockSupported] = useState(false);
    
    // Refs
    const timerRef = useRef(null);
    const speechRef = useRef(null);
    const sessionCreationRef = useRef(false);
    const navigationBlockerRef = useRef(false);
    const wakeLockRef = useRef(null);
    const sessionStateRef = useRef({ sessionId: null, sessionComplete: false, currentStepIndex: 0, timeRemaining: 0 });
    const cleanupPausedRef = useRef(null); // Track which session ID we've already paused in cleanup

    // Update session context for event emitter
    const updateSessionContext = useCallback(() => {
        // Don't update context if there's no draft loaded yet
        if (!draft || !sessionId) {
            return;
        }
        
        // Calculate total duration from draft steps
        const totalDurationMinutes = draft?.steps?.reduce((total, step) => {
            const timerInfo = parseTimerInfo(step.timer_info);
            const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
            return total + durationInSeconds;
        }, 0) / 60 || 0;
        
        const context = {
            sessionId,
            userId: user?.uid,
            recipeId: isRecipe ? recipeId : null,
            draftId: isRecipe ? null : draftId,
            currentStep: currentStepIndex,
            totalSteps: draft?.steps?.length || 0,
            totalDurationMinutes: Math.ceil(totalDurationMinutes),
            phase: sessionComplete ? 'completed' : (showPrerequisites ? 'prerequisites' : 'active')
        };
        
        liveSessionEvents.setSessionContext(context);
        
        // Also set context for Chef Reminders if enabled
        if (chefRemindersEnabled) {
            chefReminders.setSessionContext(context);
        }
    }, [sessionId, user?.uid, recipeId, draftId, currentStepIndex, draft?.steps?.length, sessionComplete, showPrerequisites, isRecipe, chefRemindersEnabled, draft]);

    // Placeholder functions for photo and note events (to be implemented)
    const handlePhotoAdded = useCallback((photoData) => {
        liveSessionEvents.emitPhotoAdded(photoData, {
            stepIndex: currentStepIndex,
            sessionId: sessionId
        });
    }, [currentStepIndex, sessionId]);

    const handleNoteAdded = useCallback((noteData) => {
        liveSessionEvents.emitNoteAdded(noteData, {
            stepIndex: currentStepIndex,
            sessionId: sessionId
        });
    }, [currentStepIndex, sessionId]);

    // Chef Reminders callbacks
    const handlePhotoReminder = useCallback((reminder) => {
        setCurrentReminder(reminder);
        setShowReminderModal(true);
    }, []);

    const handleCheckInReminder = useCallback((reminder) => {
        setCurrentReminder(reminder);
        setShowReminderModal(true);
    }, []);

    const handleUnifiedReminderResponse = useCallback(async (reminder, response, photoData = null) => {
        try {
            if (sessionId && user) {
                const token = await user.getIdToken();
                
                // Handle photo if provided
                if (photoData) {
                    // Add to session report (always)
                    await chefReminders.addPhotoTimelineEntry({
                        ...photoData,
                        imageData: photoData.imageData
                    }, reminder);
                    
                    // If this is a recipe session and recipe has no images, add image to recipe
                    if (isRecipe && recipeId && recipe && (!recipe.images || recipe.images.length === 0)) {
                        try {
                            // Convert base64 data URL to blob, then to File for upload
                            let imageFile;
                            if (photoData.blob) {
                                // If we have a blob, convert to File
                                imageFile = new File([photoData.blob], `photo_${Date.now()}.jpg`, { type: photoData.blob.type || 'image/jpeg' });
                            } else if (photoData.imageData && photoData.imageData.startsWith('data:')) {
                                // Convert base64 data URL to blob
                                const response = await fetch(photoData.imageData);
                                const blob = await response.blob();
                                imageFile = new File([blob], `photo_${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' });
                            }
                            
                            if (imageFile) {
                                // Upload image
                                const imageUrl = await uploadImage(imageFile, token);
                                
                                // Update recipe with the new image
                                const formData = new FormData();
                                formData.append('images', imageFile);
                                
                                // Get current recipe data to preserve it
                                const currentRecipe = await getRecipeById(recipeId);
                                const formDataForUpdate = new FormData();
                                
                                // Add all existing recipe fields
                                formDataForUpdate.append('name', currentRecipe.name || recipe.name || '');
                                formDataForUpdate.append('category', currentRecipe.category || recipe.category || '');
                                if (currentRecipe.cooking_time || recipe.cooking_time) {
                                    formDataForUpdate.append('cooking_time', currentRecipe.cooking_time || recipe.cooking_time);
                                }
                                
                                // Add the new image URL to existing images
                                const existingImages = currentRecipe.images || recipe.images || [];
                                const updatedImages = [...existingImages, imageUrl];
                                updatedImages.forEach(img => {
                                    formDataForUpdate.append('images', typeof img === 'string' ? img : img.url || img);
                                });
                                
                                // Update recipe
                                await updateRecipe(recipeId, formDataForUpdate, token);
                                
                                // Update local recipe state
                                setRecipe(prev => ({
                                    ...prev,
                                    images: updatedImages
                                }));
                            }
                        } catch (error) {
                            console.error('Error adding image to recipe:', error);
                            // Don't fail the whole operation if recipe update fails
                        }
                    }
                }
                
                // Handle check-in response if provided
                if (response && response.trim()) {
                    await chefReminders.addCheckInTimelineEntry(response, reminder);
                }
                
                // console.log('📸💬 Unified reminder response saved to backend');
            }
        } catch (error) {
            console.error('Error saving unified reminder response:', error);
        }
    }, [sessionId, user, isRecipe, recipeId, recipe]);

    // Legacy handlers for backward compatibility
    const handlePhotoTaken = useCallback(async (reminder, photoData = null) => {
        try {
            if (photoData) {
                // Photo data already provided (from unified modal)
                if (sessionId && user) {
                    const token = await user.getIdToken();
                    await chefReminders.addPhotoTimelineEntry({
                        ...photoData,
                        imageData: photoData.imageData
                    }, reminder);
                    
                    // If this is a recipe session and recipe has no images, add image to recipe
                    if (isRecipe && recipeId && recipe && (!recipe.images || recipe.images.length === 0)) {
                        try {
                            // Convert base64 data URL to blob, then to File for upload
                            let imageFile;
                            if (photoData.blob) {
                                // If we have a blob, convert to File
                                imageFile = new File([photoData.blob], `photo_${Date.now()}.jpg`, { type: photoData.blob.type || 'image/jpeg' });
                            } else if (photoData.imageData && photoData.imageData.startsWith('data:')) {
                                // Convert base64 data URL to blob
                                const response = await fetch(photoData.imageData);
                                const blob = await response.blob();
                                imageFile = new File([blob], `photo_${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' });
                            }
                            
                            if (imageFile) {
                                // Upload image
                                const imageUrl = await uploadImage(imageFile, token);
                                
                                // Update recipe with the new image
                                const currentRecipe = await getRecipeById(recipeId);
                                const formDataForUpdate = new FormData();
                                
                                // Add all existing recipe fields
                                formDataForUpdate.append('name', currentRecipe.name || recipe.name || '');
                                formDataForUpdate.append('category', currentRecipe.category || recipe.category || '');
                                if (currentRecipe.cooking_time || recipe.cooking_time) {
                                    formDataForUpdate.append('cooking_time', currentRecipe.cooking_time || recipe.cooking_time);
                                }
                                
                                // Add the new image URL to existing images
                                const existingImages = currentRecipe.images || recipe.images || [];
                                const updatedImages = [...existingImages, imageUrl];
                                updatedImages.forEach(img => {
                                    formDataForUpdate.append('images', typeof img === 'string' ? img : img.url || img);
                                });
                                
                                // Update recipe
                                await updateRecipe(recipeId, formDataForUpdate, token);
                                
                                // Update local recipe state
                                setRecipe(prev => ({
                                    ...prev,
                                    images: updatedImages
                                }));
                            }
                        } catch (error) {
                            console.error('Error adding image to recipe:', error);
                            // Don't fail the whole operation if recipe update fails
                        }
                    }
                }
            } else {
                // Legacy photo capture flow
                const blob = await photoCapture.capturePhotoWithUI();
                const compressedBlob = await photoCapture.compressImage(blob, 1024, 0.8);
                const photoData = photoCapture.createPhotoData(compressedBlob, reminder);
                const base64Data = await photoCapture.blobToBase64(compressedBlob);
                
                if (sessionId && user) {
                    const token = await user.getIdToken();
                    await chefReminders.addPhotoTimelineEntry({
                        ...photoData,
                        imageData: base64Data
                    }, reminder);
                    
                    // If this is a recipe session and recipe has no images, add image to recipe
                    if (isRecipe && recipeId && recipe && (!recipe.images || recipe.images.length === 0)) {
                        try {
                            // Convert blob to File for upload
                            const imageFile = new File([compressedBlob], `photo_${Date.now()}.jpg`, { type: compressedBlob.type || 'image/jpeg' });
                            
                            // Upload image
                            const imageUrl = await uploadImage(imageFile, token);
                            
                            // Update recipe with the new image
                            const currentRecipe = await getRecipeById(recipeId);
                            const formDataForUpdate = new FormData();
                            
                            // Add all existing recipe fields
                            formDataForUpdate.append('name', currentRecipe.name || recipe.name || '');
                            formDataForUpdate.append('category', currentRecipe.category || recipe.category || '');
                            if (currentRecipe.cooking_time || recipe.cooking_time) {
                                formDataForUpdate.append('cooking_time', currentRecipe.cooking_time || recipe.cooking_time);
                            }
                            
                            // Add the new image URL to existing images
                            const existingImages = currentRecipe.images || recipe.images || [];
                            const updatedImages = [...existingImages, imageUrl];
                            updatedImages.forEach(img => {
                                formDataForUpdate.append('images', typeof img === 'string' ? img : img.url || img);
                            });
                            
                            // Update recipe
                            await updateRecipe(recipeId, formDataForUpdate, token);
                            
                            // Update local recipe state
                            setRecipe(prev => ({
                                ...prev,
                                images: updatedImages
                            }));
                        } catch (error) {
                            console.error('Error adding image to recipe:', error);
                            // Don't fail the whole operation if recipe update fails
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error capturing photo:', error);
            sessionReport.recordReminder(reminder, 'dismissed');
        }
    }, [sessionId, user, isRecipe, recipeId, recipe]);

    const handleCheckInResponse = useCallback(async (reminder, response) => {
        try {
            if (sessionId && user) {
                const token = await user.getIdToken();
                await chefReminders.addCheckInTimelineEntry(response, reminder);
            }
        } catch (error) {
            console.error('Error saving check-in:', error);
        }
    }, [sessionId, user]);

    const handleReminderDismiss = useCallback((reminder) => {
        sessionReport.recordReminder(reminder, 'dismissed');
        // console.log('🔔 Reminder dismissed');
    }, []);

    // AI Tips handlers
    const generateTipsForSession = useCallback(async (sessionId) => {
        if (!user || !sessionId || tipsGenerated) return;
        
        try {
            const token = await user.getIdToken();
            const result = await aiTipsService.generateTips(sessionId, token);
            
            if (result.tips && result.tips.length > 0) {
                setAiTips(result.tips);
                setTipsGenerated(true);
            }
        } catch (error) {
            console.error('❌ Error generating AI tips:', error);
            // Don't block session if tips fail
        }
    }, [user, tipsGenerated]);

    const checkTipsForStep = useCallback((stepIndex) => {
        if (aiTips.length === 0) return;
        
        const tipsForStep = aiTips.filter(tip => tip.stepIndex === stepIndex && !tip.shown);
        
        if (tipsForStep.length > 0) {
            // Show the first tip for this step
            const tipToShow = tipsForStep[0];
            setCurrentTip(tipToShow);
            setShowTipPopup(true);
            
            // Track tip interaction in session report
            sessionReport.addTipInteraction(tipToShow, stepIndex);
        }
    }, [aiTips, sessionReport]);

    const handleTipDismiss = useCallback(() => {
        setShowTipPopup(false);
        setCurrentTip(null);
    }, []);

    const handleTipFeedback = useCallback(async (helpful) => {
        if (!currentTip || !sessionId || !user) return;
        
        try {
            const token = await user.getIdToken();
            await aiTipsService.submitTipFeedback(sessionId, currentTip.id, helpful, token);
            
            // Also track feedback in session report
            const feedback = helpful ? 'helpful' : 'not_helpful';
            sessionReport.updateTipFeedback(currentTip.id, feedback);
            
            // Mark tip as shown and update feedback
            setAiTips(prevTips => 
                prevTips.map(tip => 
                    tip.id === currentTip.id 
                        ? { ...tip, shown: true, feedback }
                        : tip
                )
            );
            
            // Close the tip popup
            setShowTipPopup(false);
            setCurrentTip(null);
            
        } catch (error) {
            console.error('Error submitting tip feedback:', error);
        }
    }, [currentTip, sessionId, user, sessionReport]);

    const handleResumeSession = useCallback(async () => {
        if (!pausedSession || !user) return;
        
        try {
            const resumeStep = pausedSession.current_step || 0;
            
            // Call API to unpause the session in the database
            const token = await user.getIdToken();
            await resumeCookingSession(pausedSession.id, token);
            
            // Close modal
            setShowResumeModal(false);
            
            // Restore session state to EXACT step
            setCurrentStepIndex(resumeStep);
            
            // Restore timer if there was time remaining
            if (pausedSession.time_remaining && pausedSession.time_remaining > 0) {
                setTimeRemaining(pausedSession.time_remaining);
                // Don't auto-start timer on resume - let user start it
                setIsTimerActive(false);
            } else if (draft?.steps?.[resumeStep]) {
                // Set up timer for current step if no remaining time
                const steps = getStepsForDisplay();
                const currentStep = steps[resumeStep];
                const timerInfo = parseTimerInfo(currentStep.timer_info);
                const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                const duration = hasTimer ? timerInfo.duration : (currentStep.duration_sec || 0);
                
                if (duration > 0) {
                    setTimeRemaining(duration);
                    setIsTimerActive(false); // Don't auto-start
                }
            }
            
            // CRITICAL: Skip prerequisites when resuming
            setShowPrerequisites(false);
            
            // CRITICAL: Set session context immediately for resumed session
            // Calculate total duration from draft steps
            const totalDurationMinutes = draft?.steps?.reduce((total, step) => {
                const timerInfo = parseTimerInfo(step.timer_info);
                const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
                return total + durationInSeconds;
            }, 0) / 60 || 0;
            
            const contextForReminders = {
                sessionId: pausedSession.id,
                userId: user?.uid,
                recipeId: isRecipe ? recipeId : null,
                draftId: isRecipe ? null : draftId,
                currentStep: resumeStep,
                totalSteps: draft?.steps?.length || 0,
                totalDurationMinutes: Math.ceil(totalDurationMinutes),
                phase: 'active'
            };
            
            liveSessionEvents.setSessionContext(contextForReminders);
            if (chefRemindersEnabled) {
                chefReminders.setSessionContext(contextForReminders);
            }
            
            // Emit session start event for resumed session
            liveSessionEvents.emitSessionStart({
                isResumed: true,
                sessionData: pausedSession
            });
            
            // CRITICAL: Set session start time for resumed session
            // Use the original session start time if available, otherwise use current time
            const sessionStartTime = pausedSession.started_at ? new Date(pausedSession.started_at).getTime() : Date.now();
            setSessionStartTime(sessionStartTime);
            setLastResumeTime(Date.now());
            
            // Generate tips if they don't exist yet
            if (!tipsGenerated && pausedSession.id) {
                generateTipsForSession(pausedSession.id);
            }
            
            // Session will continue from where it left off
            setPausedSession(null);
            setLoading(false);
            
        } catch (error) {
            console.error('Error resuming session:', error);
            alert('Failed to resume session. Please try again.');
        }
    }, [pausedSession, user, tipsGenerated, generateTipsForSession, draft, chefRemindersEnabled, isRecipe, recipeId, draftId]);

    const handlePausedResume = useCallback(async () => {
        if (!pausedSession || !user) return;
        
        try {
            const resumeStep = pausedSession.current_step || 0;
            
            // Call API to unpause the session in the database
            const token = await user.getIdToken();
            await resumeCookingSession(pausedSession.id, token);
            
            // Close modal
            setShowPausedModal(false);
            
            // Restore session state to EXACT step
            setCurrentStepIndex(resumeStep);
            
            // Restore timer if there was time remaining
            if (pausedSession.time_remaining && pausedSession.time_remaining > 0) {
                setTimeRemaining(pausedSession.time_remaining);
                // Don't auto-start timer on resume - let user start it
                setIsTimerActive(false);
            } else if (draft?.steps?.[resumeStep]) {
                // Set up timer for current step if no remaining time
                const steps = getStepsForDisplay();
                const currentStep = steps[resumeStep];
                const timerInfo = parseTimerInfo(currentStep.timer_info);
                const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                const duration = hasTimer ? timerInfo.duration : (currentStep.duration_sec || 0);
                
                if (duration > 0) {
                    setTimeRemaining(duration);
                    setIsTimerActive(false); // Don't auto-start
                }
            }
            
            // CRITICAL: Skip prerequisites when resuming
            setShowPrerequisites(false);
            
            // CRITICAL: Set session context immediately for resumed session
            // Calculate total duration from draft steps
            const totalDurationMinutes = draft?.steps?.reduce((total, step) => {
                const timerInfo = parseTimerInfo(step.timer_info);
                const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
                return total + durationInSeconds;
            }, 0) / 60 || 0;
            
            const contextForReminders = {
                sessionId: pausedSession.id,
                userId: user?.uid,
                recipeId: isRecipe ? recipeId : null,
                draftId: isRecipe ? null : draftId,
                currentStep: resumeStep,
                totalSteps: draft?.steps?.length || 0,
                totalDurationMinutes: Math.ceil(totalDurationMinutes),
                phase: 'active'
            };
            
            liveSessionEvents.setSessionContext(contextForReminders);
            if (chefRemindersEnabled) {
                chefReminders.setSessionContext(contextForReminders);
            }
            
            // Emit session start event for resumed session
            liveSessionEvents.emitSessionStart({
                isResumed: true,
                sessionData: pausedSession
            });
            
            // Generate tips if they don't exist yet
            if (!tipsGenerated && pausedSession.id) {
                generateTipsForSession(pausedSession.id);
            }
            
            // Session will continue from where it left off
            setPausedSession(null);
            setLoading(false);
            
        } catch (error) {
            console.error('Error resuming paused session:', error);
            alert('Failed to resume session. Please try again.');
        }
    }, [pausedSession, user, tipsGenerated, generateTipsForSession, draft, chefRemindersEnabled, isRecipe, recipeId, draftId]);

    const createNewSession = useCallback(async () => {
        if (!user || !draft) return;
        
        try {
            const token = await user.getIdToken();
            let session; // Declare at function scope
            
            if (isRecipe) {
                // Create new cooking session from recipe
                // This will handle 409 responses gracefully and return existing session
                session = await createCookingSessionFromRecipe(recipeId, token);
                setSessionId(session.id);
                
                // Set session context immediately with proper duration data
                const totalDurationMinutes = draft?.steps?.reduce((total, step) => {
                    const timerInfo = parseTimerInfo(step.timer_info);
                    const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
                    return total + durationInSeconds;
                }, 0) / 60 || 0;
                
                const contextForReminders = {
                    sessionId: session.id,
                    userId: user?.uid,
                    recipeId: recipeId,
                    draftId: null,
                    currentStep: 0,
                    totalSteps: draft?.steps?.length || 0,
                    totalDurationMinutes: Math.ceil(totalDurationMinutes),
                    phase: 'active'
                };
                
                liveSessionEvents.setSessionContext(contextForReminders);
                if (chefRemindersEnabled) {
                    chefReminders.setSessionContext(contextForReminders);
                }
                
                // Generate tips for new session
                if (!tipsGenerated) {
                    generateTipsForSession(session.id);
                }
            } else {
                // Create new cooking session from draft
                session = await createCookingSession(draft, token);
                setSessionId(session.id);
                
                // Set session context immediately with proper duration data
                const totalDurationMinutes = draft?.steps?.reduce((total, step) => {
                    const timerInfo = parseTimerInfo(step.timer_info);
                    const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
                    return total + durationInSeconds;
                }, 0) / 60 || 0;
                
                const contextForReminders = {
                    sessionId: session.id,
                    userId: user?.uid,
                    recipeId: null,
                    draftId: draftId,
                    currentStep: 0,
                    totalSteps: draft?.steps?.length || 0,
                    totalDurationMinutes: Math.ceil(totalDurationMinutes),
                    phase: 'active'
                };
                
                liveSessionEvents.setSessionContext(contextForReminders);
                if (chefRemindersEnabled) {
                    chefReminders.setSessionContext(contextForReminders);
                }
                
                // Generate tips for new session
                if (!tipsGenerated) {
                    generateTipsForSession(session.id);
                }
            }
            
            // Initialize session report
            sessionReport.initializeSession(session.id);
            
            // Emit session start event
            liveSessionEvents.emitSessionStart({
                isNewSession: true,
                sessionData: { id: session.id }
            });
            
            logEvent('session.started', {
                sessionId: session.id,
                recipeId: recipeId || null,
                draftId: draftId || null
            });
            logDebug('[LiveSession]', 'Session created', { 
                sessionId: session.id, 
                recipeId: recipeId || null, 
                draftId: draftId || null 
            });
            
            setLoading(false);
        } catch (error) {
            logError('[LiveSession]', 'Error creating new session', error);
            logEvent('session.create.failed', {
                recipeId: recipeId || null,
                draftId: draftId || null,
                error: error.message || 'Unknown error'
            });
            alert('Failed to create new session. Please try again.');
        }
    }, [user, draft, isRecipe, recipeId, draftId, chefRemindersEnabled, tipsGenerated, generateTipsForSession, sessionReport, liveSessionEvents, chefReminders]);

    const handlePausedRestart = useCallback(async () => {
        if (!pausedSession || !user) return;
        
        try {
            // Delete the paused session
            const token = await user.getIdToken();
            await deleteCookingSession(pausedSession.id, token);
            
            // Close modal
            setShowPausedModal(false);
            
            // Clear session state
            setPausedSession(null);
            setSessionId(null);
            setCurrentStepIndex(0);
            setTimeRemaining(0);
            setIsTimerActive(false);
            setIsPaused(false);
            setSessionComplete(false);
            
            // Clear session report and reminders
            sessionReport.clearReport();
            chefReminders.cleanup();
            
            // Start fresh session
            await createNewSession();
            
        } catch (error) {
            console.error('Error restarting paused session:', error);
            alert('Failed to restart session. Please try again.');
        }
    }, [pausedSession, user, createNewSession]);

    const handleRestartSession = useCallback(async () => {
        if (!pausedSession || !user) return;
        
        try {
            // Delete the old incomplete session completely
            const token = await user.getIdToken();
            await deleteCookingSession(pausedSession.id, token);
            
            // Close modal
            setShowResumeModal(false);
            
            // Clear all session state
            setPausedSession(null);
            setSessionId(null);
            setCurrentStepIndex(0);
            setTimeRemaining(0);
            setIsTimerActive(false);
            setIsPaused(false);
            setSessionComplete(false);
            setTipsGenerated(false);
            setAiTips([]);
            
            // Clear session report and reminders
            sessionReport.clearReport();
            chefReminders.cleanup();
            
            // Create brand new session with fresh tips
            await createNewSession();
            
        } catch (error) {
            console.error('❌ Error restarting session:', error);
            alert('Failed to restart session. Please try again.');
        }
    }, [pausedSession, user, createNewSession]);

    const handleChefSummariesComplete = useCallback((structuredReport) => {
        setChefSummariesReport(structuredReport);
        setShowChefSummariesModal(false);
        
        // Store the structured report in localStorage for SessionReflection to pick up
        localStorage.setItem('chefSummariesReport', JSON.stringify(structuredReport));
        
        // Show posting modal instead of navigating directly
        setShowPostingModal(true);
    }, []);

    const handlePostingComplete = useCallback(async (postData) => {
        // TODO: Implement post creation API call here
        // For now, just navigate to the appropriate page
        
        // Navigate to recipe detail page
        if (isRecipe) {
            // Live Sessions v2: If a fork was created, redirect to the fork's recipe detail page
            if (sessionData?.fork_id && sessionData?.fork_state === 'confirmed') {
                navigate(`/recipes/${sessionData.fork_id}`);
            } else {
                navigate(`/recipes/${sourceId}`);
            }
        } else {
            navigate(`/recipes/${sourceId}`);
        }
    }, [chefSummariesEnabled, isRecipe, sourceId, navigate, sessionData]);

    // Wake Lock API functions
    const requestWakeLock = useCallback(async () => {
        if (!wakeLockSupported || !('wakeLock' in navigator)) {
            console.warn('Wake Lock API not supported');
            return false;
        }

        try {
            const wakeLock = await navigator.wakeLock.request('screen');
            wakeLockRef.current = wakeLock;
            
            wakeLock.addEventListener('release', () => {
                // console.log('Wake Lock was released');
                wakeLockRef.current = null;
            });
            
            // console.log('Wake Lock acquired successfully');
            return true;
        } catch (err) {
            console.error('Failed to acquire Wake Lock:', err);
            return false;
        }
    }, [wakeLockSupported]);

    const releaseWakeLock = useCallback(() => {
        if (wakeLockRef.current) {
            wakeLockRef.current.release();
            wakeLockRef.current = null;
            // console.log('Wake Lock released');
        }
    }, []);

    const toggleAlwaysOn = useCallback(async () => {
        if (alwaysOnEnabled) {
            // Turn off always-on
            releaseWakeLock();
            setAlwaysOnEnabled(false);
        } else {
            // Turn on always-on
            const success = await requestWakeLock();
            if (success) {
                setAlwaysOnEnabled(true);
            } else {
                alert('Unable to prevent screen from turning off. This feature may not be supported on your device.');
            }
        }
    }, [alwaysOnEnabled, requestWakeLock, releaseWakeLock]);
    const speechQueueRef = useRef([]);
    const isSpeakingRef = useRef(false);
    const questionTimeoutRef = useRef(null);
    const voiceDisabledRef = useRef(false);
    const lastSpeechEndTimeRef = useRef(Date.now());
    const voiceRestartTimeoutRef = useRef(null);
    const voiceEnabledRef = useRef(false);
    const voiceConsentGivenRef = useRef(false);
    const isWaitingForQuestionRef = useRef(false);
    
    // Audio management refs - completely overhauled
    const audioManagerRef = useRef({
        alarmInterval: null,
        audioContext: null,
        oscillator: null,
        customAudio: null,
        isAlarmActive: false,
        isPlaying: false,
        volume: 0.5 // Default volume
    });

    useEffect(() => {
        voiceDisabledRef.current = voiceDisabled;
    }, [voiceDisabled]);

    useEffect(() => {
        lastSpeechEndTimeRef.current = lastSpeechEndTime;
    }, [lastSpeechEndTime]);

    useEffect(() => {
        voiceEnabledRef.current = voiceEnabled;
    }, [voiceEnabled]);

    useEffect(() => {
        voiceConsentGivenRef.current = voiceConsentGiven;
    }, [voiceConsentGiven]);

    useEffect(() => {
        isWaitingForQuestionRef.current = isWaitingForQuestion;
    }, [isWaitingForQuestion]);

    useEffect(() => {
        // console.log('LiveSession useEffect triggered:', {
        //     sourceId,
        //     isRecipe,
        //     hasUser: !!user,
        //     sessionCreationRef: sessionCreationRef.current
        // });
        
        if (!user) {
            navigate('/login');
            return;
        }
        
        fetchRecipeOrDraft();
        fetchUserPreferences();
        
        // Start session timing
        const startTime = Date.now();
        setSessionStartTime(startTime);
        setLastResumeTime(startTime);
        
        // Cleanup function to handle when user leaves without completing
        return () => {
            // Don't reset session creation flag here - it causes session recreation
            // sessionCreationRef.current = false;
            
            // Use ref to get current session state to avoid stale closure
            const { sessionId: currentSessionId, sessionComplete: isComplete, currentStepIndex: stepIndex, timeRemaining: remaining } = sessionStateRef.current;
            
            // If user leaves without completing, pause the session so they can resume later
            // Only pause if we haven't already paused this session ID (prevents duplicate pauses in dev mode)
            if (currentSessionId && !isComplete && cleanupPausedRef.current !== currentSessionId) {
                console.log('User left session without completing, pausing session:', currentSessionId);
                cleanupPausedRef.current = currentSessionId; // Mark this session as paused
                
                // Pause the session asynchronously
                (async () => {
                    try {
                        const token = await user.getIdToken();
                        await pauseCookingSession(currentSessionId, stepIndex, remaining, token);
                        console.log('Session paused successfully on cleanup');
                    } catch (error) {
                        console.error('Error pausing session on cleanup:', error);
                    }
                })();
                
                // Emit session end event for abandoned session
                liveSessionEvents.emitSessionEnd({
                    wasCompleted: false,
                    wasAbandoned: true,
                    totalElapsedTime: totalElapsedTime
                });
            }
            
            // Note: Don't clear ALL listeners here because chefReminders is a singleton
            // that registers its listeners once. Each service should manage its own cleanup.
            
            // Cleanup Chef Reminders (this will unsubscribe its own listeners)
            chefReminders.cleanup();
            
            // Clear session report
            sessionReport.clearReport();
        };
    }, [sourceId, isRecipe, user, navigate]);

    // Fetch agent settings when user loads
    useEffect(() => {
        if (user) {
            fetchAgentSettings();
        }
    }, [user]);

    // Update voice service trigger phrase when agent settings change
    useEffect(() => {
        if (agentSettings.agent_trigger) {
            voiceService.updateTriggerPhrase(agentSettings.agent_trigger);
        }
    }, [agentSettings.agent_trigger]);

    // Update session context whenever relevant state changes
    useEffect(() => {
        // DON'T update context if we're showing the resume modal
        // The context will be set when user clicks Resume/Restart
        if (showResumeModal) {
            return;
        }
        updateSessionContext();
    }, [updateSessionContext, showResumeModal]);

    // Keep sessionStateRef updated with current values for cleanup
    useEffect(() => {
        sessionStateRef.current = {
            sessionId,
            sessionComplete,
            currentStepIndex,
            timeRemaining
        };
        
        // Reset cleanup flag when session completes or changes
        if (sessionComplete || !sessionId) {
            cleanupPausedRef.current = null;
        }
    }, [sessionId, sessionComplete, currentStepIndex, timeRemaining]);

    // Initialize Chef Reminders
    useEffect(() => {
        if (userProfile?.agent_preferences?.chef_reminders) {
            setChefRemindersEnabled(true);
            chefReminders.setEnabled(true);
            chefReminders.setCallbacks({
                onPhotoReminder: handlePhotoReminder, // Unified reminders use photo callback
                onCheckInReminder: handleCheckInReminder
            });
        } else {
            setChefRemindersEnabled(false);
            chefReminders.setEnabled(false);
        }
    }, [userProfile?.agent_preferences?.chef_reminders, handlePhotoReminder, handleCheckInReminder]);

    // Set user token for Chef Reminders when user changes
    useEffect(() => {
        if (user && chefRemindersEnabled) {
            user.getIdToken().then(token => {
                chefReminders.setUserToken(token);
            });
        }
    }, [user, chefRemindersEnabled]);

    // Initialize Chef Summaries
    useEffect(() => {
        // Always enable Chef Summaries for base sessions (Cook Now flow) or recipe sessions
        // Base sessions are identified by having a kernel_id in the draft
        const isBaseSession = !isRecipe && draft?.kernel_id;
        if (isBaseSession || isRecipe || userProfile?.agent_preferences?.chef_summaries) {
            setChefSummariesEnabled(true);
            // console.log('📝 Chef Summaries enabled for this session');
        } else {
            setChefSummariesEnabled(false);
            // console.log('📝 Chef Summaries disabled for this session');
        }
    }, [userProfile?.agent_preferences?.chef_summaries, isRecipe, draft?.kernel_id]);

    // Initialize AI Tips (controlled by chef_recommendations setting)
    useEffect(() => {
        if (userProfile?.agent_preferences?.chef_recommendations) {
            setAiTipsEnabled(true);
        } else {
            setAiTipsEnabled(false);
        }
    }, [userProfile?.agent_preferences?.chef_recommendations]);

    // Generate AI tips when session is created (only if enabled)
    useEffect(() => {
        if (sessionId && !tipsGenerated && aiTipsEnabled) {
            generateTipsForSession(sessionId);
        }
    }, [sessionId, tipsGenerated, aiTipsEnabled, generateTipsForSession]);

    // Check for tips when step changes
    useEffect(() => {
        if (!showPrerequisites && currentStepIndex >= 0) {
            checkTipsForStep(currentStepIndex);
        }
    }, [currentStepIndex, showPrerequisites, checkTipsForStep]);

    // Initialize session report when session starts
    useEffect(() => {
        if (sessionId) {
            sessionReport.initializeSession(sessionId);
        }
    }, [sessionId]);

    // Check Wake Lock API support
    useEffect(() => {
        if ('wakeLock' in navigator) {
            setWakeLockSupported(true);
            // console.log('Wake Lock API is supported');
        } else {
            setWakeLockSupported(false);
            // console.log('Wake Lock API is not supported');
        }
    }, []);

    // Cleanup Wake Lock on unmount
    useEffect(() => {
        return () => {
            releaseWakeLock();
        };
    }, [releaseWakeLock]);

    // Sync chef position from localStorage
    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem('chefTabPosition');
            if (saved) {
                setChefPosition(JSON.parse(saved));
            }
        };

        // Listen for storage changes (when chef is moved)
        window.addEventListener('storage', handleStorageChange);
        
        // Also poll periodically for same-tab updates
        const interval = setInterval(() => {
            handleStorageChange();
        }, 500);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'ArrowRight' || event.key === ' ') {
                event.preventDefault();
                nextStep();
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                previousStep();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                navigate(-1);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentStepIndex, draft]);

    // Multi-timer effect - handles all active timers
    useEffect(() => {
        const activeTimerCount = activeTimers.filter(t => t.isActive && t.remaining > 0).length;
        
        if (activeTimerCount > 0) {
            timerRef.current = setInterval(() => {
                setActiveTimers(prev => {
                    let anyTimerFinished = false;
                    const updated = prev.map(timer => {
                        if (!timer.isActive || timer.remaining <= 0) {
                            return timer;
                        }
                        
                        const newRemaining = timer.remaining - 1;
                        
                        if (newRemaining <= 0) {
                            anyTimerFinished = true;
                            
                            // Emit timer tick event for timer completion
                            liveSessionEvents.emitTimerTick(0, {
                                timerFinished: true,
                                stepIndex: timer.stepIndex !== null ? timer.stepIndex : currentStepIndex,
                                timerTitle: timer.title
                            });
                            
                            // Start alarm if not muted
                            if (!timer.isMuted && userPreferences.alarm_enabled) {
                                setTimerAlarmActive(true);
                                audioManager.startRepeatingAlarm();
                            }
                            
                            return { ...timer, remaining: 0, isActive: false };
                        }
                        
                        // Emit timer tick event only when a minute passes
                        if (newRemaining > 0 && newRemaining % 60 === 0) {
                            liveSessionEvents.emitTimerTick(newRemaining, {
                                stepIndex: timer.stepIndex !== null ? timer.stepIndex : currentStepIndex,
                                isActive: true,
                                minutesRemaining: Math.floor(newRemaining / 60),
                                timerTitle: timer.title
                            });
                        }
                        
                        return { ...timer, remaining: newRemaining };
                    });
                    
                    return updated;
                });
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [activeTimers, currentStepIndex, userPreferences.alarm_enabled]);

    // Legacy single timer effect (for backward compatibility)
    useEffect(() => {
        if (isTimerActive && timeRemaining > 0) {
            timerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        setIsTimerActive(false);
                        setTimerAlarmActive(true);
                        audioManager.startRepeatingAlarm();
                        
                        // Emit timer tick event for timer completion
                        liveSessionEvents.emitTimerTick(0, {
                            timerFinished: true,
                            stepIndex: currentStepIndex
                        });
                        
                        return 0;
                    }
                    
                    // Emit timer tick event only when a minute passes
                    const newTime = prev - 1;
                    if (newTime > 0 && newTime % 60 === 0) {
                        liveSessionEvents.emitTimerTick(newTime, {
                            stepIndex: currentStepIndex,
                            isActive: true,
                            minutesRemaining: Math.floor(newTime / 60)
                        });
                    }
                    
                    return prev - 1;
                });
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [isTimerActive, timeRemaining, currentStepIndex]);

    // Session elapsed time tracking effect
    useEffect(() => {
        let elapsedTimer = null;
        
        // console.log('Elapsed time tracking effect:', {
        //     sessionStartTime,
        //     isPaused,
        //     sessionComplete,
        //     totalElapsedTime
        // });
        
        if (sessionStartTime && !isPaused && !sessionComplete) {
            elapsedTimer = setInterval(() => {
                const currentTime = Date.now();
                const totalElapsed = Math.floor((currentTime - sessionStartTime) / 1000);
                setTotalElapsedTime(totalElapsed);
            }, 1000);
        }

        return () => {
            if (elapsedTimer) {
                clearInterval(elapsedTimer);
            }
        };
    }, [sessionStartTime, isPaused, sessionComplete]);

    // Narration effect
    useEffect(() => {
        if (narrateEnabled && draft?.steps?.[currentStepIndex]) {
            const step = draft.steps[currentStepIndex];
            speak(`${step.title}. ${step.body}`);
        } else if (!narrateEnabled) {
            // Stop any ongoing narration when disabled
            stopSpeech();
        }
    }, [currentStepIndex, narrateEnabled, draft]);

    // Cleanup speech and alarm when component unmounts or user navigates away
    useEffect(() => {
        return () => {
            stopSpeech({ resumeListening: false });
            audioManager.stopAllAudio();
        };
    }, []);

    // Auto-show ingredients popup at start and keep it open
    useEffect(() => {
        if (draft && !loading) {
            // Show ingredients popup at start and keep it open
            setShowIngredientsPopup(true);
        }
    }, [draft, loading]);

    // Check voice preference from sessionStorage (set by MicrophonePermissionModal)
    useEffect(() => {
        if (draft && !loading && agentSettingsLoaded) {
            const voicePreference = sessionStorage.getItem('chefVoiceEnabled');
            
            if (voicePreference !== null) {
                const voiceEnabledValue = voicePreference === 'true';
                setVoiceEnabled(voiceEnabledValue);
                setVoiceConsentGiven(true); // Consent was given in the permission modal
                
                // Clear the preference from sessionStorage after reading it
                sessionStorage.removeItem('chefVoiceEnabled');
                
                // If voice is enabled, check browser support
                if (voiceEnabledValue) {
                    const browserInfo = voiceService.checkBrowserSupport();
                    if (!browserInfo.hasSpeechRecognition) {
                        console.warn('Voice recognition not supported, disabling voice');
                        setVoiceEnabled(false);
                    }
                }
            } else {
                // No preference set - default to disabled (user must have skipped the modal somehow)
                setVoiceEnabled(false);
                setVoiceConsentGiven(true);
            }
        }
    }, [draft, loading, agentSettingsLoaded]);

    // Voice service event handlers
    useEffect(() => {
        if (!voiceEnabled) return;

        voiceService.setOnStart(() => {
            setVoiceListening(true);
        });

        voiceService.setOnEnd(() => {
            setVoiceListening(false);
            setVoiceTriggered(false);
        });

        voiceService.setOnResult((transcript, isTriggered) => {
            const now = Date.now();
            const timeSinceLastSpeech = now - lastSpeechEndTimeRef.current;

            // console.log('Voice result:', {
            //     transcript,
            //     isTriggered,
            //     isSpeaking: isSpeakingRef.current,
            //     timeSinceLastSpeech,
            //     lastSpeechEndTime: lastSpeechEndTimeRef.current,
            //     voiceDisabled: voiceDisabledRef.current
            // });

            if (voiceDisabledRef.current) {
                // console.log('Voice recognition disabled - ignoring all input');
                return;
            }

            if (isSpeakingRef.current) {
                // console.log('Ignoring voice input while AI is speaking');
                return;
            }
            
            if (timeSinceLastSpeech < MIN_GAP_AFTER_SPEECH_MS) {
                // console.log('Ignoring voice input too soon after AI speech');
                return;
            }
            
            setVoiceTriggered(isTriggered);
            
            if (isTriggered) {
                // console.log('Trigger phrase detected, processing command...');
                
                // Play bell sound to notify user that trigger phrase was detected
                if (!transcript) {
                    audioManager.playSound('bell', 0.3);
                }
                
                if (questionTimeoutRef.current) {
                    clearTimeout(questionTimeoutRef.current);
                    questionTimeoutRef.current = null;
                }
                
                if (!transcript) {
                    if (!isWaitingForQuestionRef.current) {
                        setWaitingForQuestionState(true);
                        questionTimeoutRef.current = setTimeout(() => {
                            // console.log('Question timeout - stopping wait for question');
                            setWaitingForQuestionState(false);
                            questionTimeoutRef.current = null;
                        }, VOICE_SILENCE_TIMEOUT_MS);
                    }
                    return;
                }

                const command = voiceCommandProcessor.processCommand(transcript, true);
                if (command) {
                    // console.log('Command processed:', command);
                    handleVoiceCommand(command);
                    setLastVoiceCommand(command.originalText);
                } else {
                    // console.log('No command recognized from:', transcript);
                    
                    if (!isWaitingForQuestionRef.current) {
                        // console.log('Waiting for question after trigger phrase...');
                        setWaitingForQuestionState(true);
                        
                        questionTimeoutRef.current = setTimeout(() => {
                            // console.log('Question timeout - stopping wait for question');
                            setWaitingForQuestionState(false);
                            questionTimeoutRef.current = null;
                        }, VOICE_SILENCE_TIMEOUT_MS);
                    }
                }
            } else if (isWaitingForQuestionRef.current) {
                // console.log('Question received:', transcript);
                setWaitingForQuestionState(false);
                
                if (questionTimeoutRef.current) {
                    clearTimeout(questionTimeoutRef.current);
                    questionTimeoutRef.current = null;
                }
                
                handleQuestionCommand(transcript);
            }
        });

        voiceService.setOnError((error) => {
            // Only log errors that are not expected/normal behavior
            if (error !== 'no-speech') {
                console.error('Voice recognition error:', error);
            }
            
            setVoiceListening(false);
            setVoiceTriggered(false);
            
            // Check browser compatibility and provide helpful error message
            const browserInfo = voiceService.checkBrowserSupport();
            // console.log('Browser compatibility info:', browserInfo);
            
            if (!browserInfo.hasSpeechRecognition) {
                speak('Voice recognition is not supported in this browser. Please use Chrome, Edge, or Safari for voice features.');
            } else if (error === 'not-allowed') {
                speak('Microphone access was denied. Please allow microphone access and try again.');
            } else if (error && error !== 'no-speech') {
                speak('Voice recognition error occurred. Please try again.');
            }
            
            // No need to handle no-speech errors - voice service handles auto-restart automatically
        });

        return () => {
            voiceService.setOnStart(null);
            voiceService.setOnEnd(null);
            voiceService.setOnResult(null);
            voiceService.setOnError(null);
        };
    }, [voiceEnabled]);

    // Start/stop voice listening based on voiceEnabled state
    useEffect(() => {
        clearVoiceRestartTimeout();

        if (voiceEnabled && voiceConsentGiven) {
            updateVoiceDisabledState(false);

            // Add a small delay to ensure the service is ready
            setTimeout(() => {
                const success = voiceService.startListening();
                if (!success) {
                    console.warn('Failed to start voice listening');
                }
            }, 100);
        } else {
            voiceService.stopListening();
        }
    }, [voiceEnabled, voiceConsentGiven]);

    // Ensure prerequisites are shown when draft loads
    useEffect(() => {
        if (draft && !loading) {
            const hasPrerequisites = draft.prerequisites && draft.prerequisites.length > 0;
            setShowPrerequisites(hasPrerequisites);
        }
    }, [draft, loading]);

    // Complete session and navigate
    const completeSessionAndNavigate = useCallback(async () => {
        setSessionComplete(true);
        
        // Emit session end event
        liveSessionEvents.emitSessionEnd({
            wasCompleted: true,
            totalElapsedTime: totalElapsedTime,
            finalStep: currentStepIndex,
            totalSteps: draft?.steps?.length || 0
        });
        
        // Mark session as completed with actual elapsed time
        if (sessionId) {
            try {
                const token = await user.getIdToken();
                
                // Calculate final elapsed time
                let finalElapsedTime = totalElapsedTime;
                if (lastResumeTime) {
                    const elapsedSinceResume = Math.floor((Date.now() - lastResumeTime) / 1000);
                    finalElapsedTime += elapsedSinceResume;
                }
                
                const updatedSession = await completeCookingSession(sessionId, token, finalElapsedTime);
                
                // Update sessionData with the response from the completion API
                if (updatedSession) {
                    setSessionData(prevData => ({
                        ...prevData,
                        ...updatedSession
                    }));
                }
            } catch (err) {
                console.error('Error completing session:', err);
            }
        }
        
        // Navigate based on session type
        if (isRecipe) {
            if (sessionId) {
                navigate(`/recipes/${sourceId}`);
            } else {
                navigate(`/recipes/${sourceId}`);
            }
        } else {
            if (draft?.id) {
                navigate(`/drafts/${sourceId}`);
            }
        }
    }, [sessionId, user, totalElapsedTime, lastResumeTime, currentStepIndex, draft, isRecipe, sourceId, navigate]);

    // Handlers for reflection modal actions
    const handleReflectionSkip = useCallback(async () => {
        // Complete session without reflection
        await completeSessionAndNavigate();
    }, [completeSessionAndNavigate]);

    const handleReflectionFinishNoPost = useCallback(async () => {
        // Complete session and navigate to recipe/draft page
        await completeSessionAndNavigate();
    }, [completeSessionAndNavigate]);

    const handleReflectionPost = useCallback(async () => {
        // Complete session and show posting modal
        await completeSessionAndNavigate();
        setShowPostingModal(true);
    }, [completeSessionAndNavigate]);

    const fetchUserPreferences = async () => {
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api'}/users/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const userData = await response.json();
                if (userData.session_preferences) {
                    setUserPreferences(userData.session_preferences);
                }
            }
        } catch (error) {
            console.error('Error fetching user preferences:', error);
        }
    };

    const saveUserPreferences = async (newPreferences) => {
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api'}/users/${user.uid}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    session_preferences: newPreferences
                })
            });
            
            if (!response.ok) {
                console.error('Error saving user preferences');
            }
        } catch (error) {
            console.error('Error saving user preferences:', error);
        }
    };

    const fetchRecipeOrDraft = async () => {
        try {
            const token = await user.getIdToken();
            let draftData; // Declare draftData in outer scope
            
            // Check if RecipeDetails just created a session for us
            const pendingSessionId = sessionStorage.getItem('pendingLiveSessionId');
            let hasPendingSession = false;
            if (pendingSessionId) {
                sessionStorage.removeItem('pendingLiveSessionId');
                setSessionId(pendingSessionId);
                sessionCreationRef.current = true; // Mark that we have a session
                hasPendingSession = true; // Track this locally for synchronous checks
                
                // Load session data to get custom ingredients
                try {
                    const sessionData = await getCookingSessionById(pendingSessionId, token);
                    console.log('🔧 Loaded session data with custom ingredients');
                    setSessionData(sessionData);
                } catch (error) {
                    console.error('Error loading session data:', error);
                }
            }
            
            if (isRecipe) {
                // Load recipe from stack
                const recipeData = await getRecipeById(sourceId);
                setRecipe(recipeData);
                
                // Check if this is a multi-component recipe
                const isMultiComponent = recipeData.isMultiComponent === true;
                let components = null;
                let assembly = null;
                
                if (isMultiComponent) {
                    // Parse components and assembly
                    components = typeof recipeData.components === 'string' ? 
                        JSON.parse(recipeData.components) : recipeData.components;
                    assembly = typeof recipeData.assembly === 'string' ? 
                        JSON.parse(recipeData.assembly) : recipeData.assembly;
                }
                
                // Convert stack recipe format to draft format for consistency
                const instructions = typeof recipeData.instructions === 'string' ? 
                    JSON.parse(recipeData.instructions) : recipeData.instructions;
                
                // Convert timer associations from stack format to step format
                const timerAssociations = recipeData.timer_associations ? 
                    (typeof recipeData.timer_associations === 'string' ? 
                        JSON.parse(recipeData.timer_associations) : recipeData.timer_associations) : [];
                
                // CRITICAL: Get timing segments from session data if available (generated by backend)
                // This ensures ALL steps get timing information, not just the first one
                let sessionTimingSegments = {};
                if (sessionData && sessionData.timing_segments) {
                    try {
                        sessionTimingSegments = typeof sessionData.timing_segments === 'string' 
                            ? JSON.parse(sessionData.timing_segments) 
                            : sessionData.timing_segments;
                        console.log('🔧 Using timing segments from session:', {
                            stepCount: Object.keys(sessionTimingSegments).length,
                            stepIndices: Object.keys(sessionTimingSegments).map(k => parseInt(k))
                        });
                    } catch (e) {
                        console.error('Error parsing session timing_segments:', e);
                    }
                }
                
                let convertedSteps = [];
                
                if (isMultiComponent && components && assembly) {
                    // Multi-component recipe: extract steps from all components, then assembly
                    let stepIndex = 0;
                    
                    // Process each component's steps
                    components.forEach((component) => {
                        const componentSteps = Array.isArray(component.steps) ? component.steps : [];
                        componentSteps.forEach((step) => {
                            const stepText = typeof step === 'string' ? step : (step.text || '');
                            // CRITICAL: Use timing segments from session if available, otherwise from step data
                            const timingSegments = sessionTimingSegments[stepIndex] || step.timing_segments || [];
                            
                            const timerAssociation = timerAssociations.find(ta => ta.stepIndex === stepIndex);
                            const durationInMinutes = timerAssociation?.duration || 0;
                            const durationInSeconds = durationInMinutes * 60;
                            
                            const timerInfo = {
                                enabled: durationInMinutes > 0,
                                duration: durationInSeconds,
                                description: timerAssociation?.description || stepText,
                                ingredients: timerAssociation?.ingredients || []
                            };
                            
                            convertedSteps.push({
                                title: `Step ${stepIndex + 1}`,
                                body: stepText,
                                duration_sec: durationInSeconds,
                                timer_info: JSON.stringify(timerInfo),
                                timing_segments: timingSegments,
                                componentName: component.name || null // Store component name for display
                            });
                            stepIndex++;
                        });
                    });
                    
                    // Process assembly steps
                    const assemblySteps = Array.isArray(assembly.steps) ? assembly.steps : [];
                    assemblySteps.forEach((step) => {
                        const stepText = typeof step === 'string' ? step : (step.text || '');
                        // CRITICAL: Use timing segments from session if available, otherwise from step data
                        const timingSegments = sessionTimingSegments[stepIndex] || step.timing_segments || [];
                        
                        const timerAssociation = timerAssociations.find(ta => ta.stepIndex === stepIndex);
                        const durationInMinutes = timerAssociation?.duration || 0;
                        const durationInSeconds = durationInMinutes * 60;
                        
                        const timerInfo = {
                            enabled: durationInMinutes > 0,
                            duration: durationInSeconds,
                            description: timerAssociation?.description || stepText,
                            ingredients: timerAssociation?.ingredients || []
                        };
                        
                        convertedSteps.push({
                            title: `Step ${stepIndex + 1}`,
                            body: stepText,
                            duration_sec: durationInSeconds,
                            timer_info: JSON.stringify(timerInfo),
                            timing_segments: timingSegments,
                            componentName: null // Assembly steps don't have a component name
                        });
                        stepIndex++;
                    });
                } else {
                    // Simple recipe: use instructions steps
                    convertedSteps = (instructions?.steps || []).map((stepText, index) => {
                        const timerAssociation = timerAssociations.find(ta => ta.stepIndex === index);
                        const durationInMinutes = timerAssociation?.duration || 0;
                        const durationInSeconds = durationInMinutes * 60; // Convert minutes to seconds
                        
                        // CRITICAL: Use timing segments from session if available
                        // This ensures ALL steps get timing information, not just the first one
                        const timingSegments = sessionTimingSegments[index] || [];
                        
                        const timerInfo = {
                            enabled: durationInMinutes > 0, // Enable timer if there's a duration
                            duration: durationInSeconds, // Store in seconds for consistency
                            description: timerAssociation?.description || stepText,
                            ingredients: timerAssociation?.ingredients || []
                        };
                        
                        return {
                            title: `Step ${index + 1}`,
                            body: stepText,
                            duration_sec: durationInSeconds, // Store in seconds
                            timer_info: JSON.stringify(timerInfo), // Ensure it's stored as a string to match database format
                            timing_segments: timingSegments, // CRITICAL: Include timing segments for all steps
                            componentName: null
                        };
                    });
                }
                
                // Collect all ingredients from components if multi-component
                let allIngredients = [];
                if (isMultiComponent && components) {
                    components.forEach((component) => {
                        const componentIngredients = Array.isArray(component.ingredients) ? component.ingredients : [];
                        allIngredients = [...allIngredients, ...componentIngredients];
                    });
                } else {
                    allIngredients = typeof recipeData.ingredients === 'string' ? 
                        JSON.parse(recipeData.ingredients) : recipeData.ingredients;
                }
                
                // Collect prerequisites from components and assembly if multi-component
                let allPrerequisites = [];
                if (isMultiComponent && components) {
                    components.forEach((component) => {
                        const componentPrereqs = Array.isArray(component.prerequisites) ? component.prerequisites : [];
                        allPrerequisites = [...allPrerequisites, ...componentPrereqs];
                    });
                    if (assembly && Array.isArray(assembly.prerequisites)) {
                        allPrerequisites = [...allPrerequisites, ...assembly.prerequisites];
                    }
                } else {
                    allPrerequisites = instructions?.prerequisites || [];
                }
                
                draftData = {
                    title: recipeData.name,
                    category: recipeData.category,
                    ingredients: allIngredients,
                    prerequisites: allPrerequisites,
                    steps: convertedSteps,
                    kernel_id: recipeData.kernel_id,
                    isMultiComponent: isMultiComponent,
                    components: components,
                    assembly: assembly
                };
                
                setDraft(draftData);
            } else {
                // Load draft recipe
                draftData = await getDraftRecipe(sourceId, token);
                setDraft(draftData);
            }
            
            // Handle session creation/resumption after draft is loaded
            const currentDraft = draftData; // Use the draftData we just loaded
            
            // Declare outside try block so it's accessible later
            let incompleteSession = null;
            
            // Only check for existing sessions if we don't already have a pending session from RecipeDetails
            // OR if we don't already have a sessionId set (prevents double-search on re-render)
            if (!hasPendingSession && !sessionId) {
                try {
                    // console.log('Checking for existing sessions...');
                    // Force refresh to ensure we get fresh data after potential deletions
                    // Add longer delay to handle race conditions with session deletions
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const existingSessions = await getCookingSessions(token, true);
                    // console.log('Total sessions found:', existingSessions.length);
                    
                    // Check for incomplete sessions (debugging disabled to reduce console noise)
                    
                    // Look for any session (paused or incomplete) for this recipe/draft
                    const existingSession = existingSessions.find(s => {
                    if (isRecipe) {
                        // Live Sessions v2: Look for sessions that are either:
                        // 1. Direct sessions for the original recipe ID
                        // 2. Sessions for forks of the original recipe (parent_recipe_id = sourceId)
                        const isDirectSession = String(s.stack_id) === String(sourceId);
                        const isForkSession = s.parent_recipe_id && String(s.parent_recipe_id) === String(sourceId);
                        const matches = (isDirectSession || isForkSession) && !s.was_completed;
                        return matches;
                    } else {
                        return String(s.draft_id) === String(sourceId) && !s.was_completed;
                    }
                });
                
                // Determine if this is a paused session or truly incomplete
                if (existingSession) {
                    if (existingSession.is_paused) {
                        // This is a paused session - user intentionally paused it
                        incompleteSession = existingSession;
                    } else {
                        // This is an incomplete session - session was never properly completed
                        incompleteSession = existingSession;
                    }
                }
                
                if (incompleteSession) {
                    
                    // Show different modals based on session state
                    if (incompleteSession.is_paused) {
                        // Show enhanced paused session modal for truly paused sessions
                        setPausedSession(incompleteSession);
                        setShowPausedModal(true);
                        setSessionId(incompleteSession.id);
                        
                        // Load session data to get custom ingredients
                        try {
                            const sessionData = await getCookingSessionById(incompleteSession.id, token);
                            console.log('🔧 Loaded paused session data with custom ingredients');
                            setSessionData(sessionData);
                        } catch (error) {
                            console.error('Error loading paused session data:', error);
                        }
                        
                        // Load existing AI tips if available
                        if (incompleteSession.ai_tips) {
                            const tips = typeof incompleteSession.ai_tips === 'string' 
                                ? JSON.parse(incompleteSession.ai_tips) 
                                : incompleteSession.ai_tips;
                            setAiTips(tips);
                            setTipsGenerated(true);
                        }
                        
                        // STOP HERE - wait for user to choose resume or restart
                        setLoading(false);
                        return;
                    } else {
                        // Show regular resume modal for incomplete sessions (not paused)
                        // This indicates the session was never properly completed
                        setPausedSession(incompleteSession);
                        setShowResumeModal(true);
                        setSessionId(incompleteSession.id);
                        
                        // Load session data to get custom ingredients
                        try {
                            const sessionData = await getCookingSessionById(incompleteSession.id, token);
                            console.log('🔧 Loaded incomplete session data with custom ingredients');
                            setSessionData(sessionData);
                        } catch (error) {
                            console.error('Error loading incomplete session data:', error);
                        }
                        
                        // Load existing AI tips if available
                        if (incompleteSession.ai_tips) {
                            const tips = typeof incompleteSession.ai_tips === 'string' 
                                ? JSON.parse(incompleteSession.ai_tips) 
                                : incompleteSession.ai_tips;
                            setAiTips(tips);
                            setTipsGenerated(true);
                        }
                        
                        // STOP HERE - wait for user to choose resume or restart
                        setLoading(false);
                        return;
                    }
                } else {
                    // No incomplete session found AND no active session
                    // This means user navigated directly to /live/recipe/{id} without clicking "Live Session" button
                    // OR RecipeDetails is still creating the session
                    // Redirect to recipe page to force proper flow
                    console.warn('⚠️ No active session found, redirecting to recipe page');
                    if (isRecipe) {
                        navigate(`/recipes/${sourceId}`);
                    } else {
                        navigate(`/drafts/${sourceId}`);
                    }
                    return;
                }
                } catch (sessionError) {
                    console.error('Error handling session:', sessionError);
                    // Reset the creation flag on error
                    sessionCreationRef.current = false;
                    // Continue without session tracking if there's an error
                }
            } else {
                const reason = hasPendingSession ? 'pending session from RecipeDetails' : 'sessionId already set';
                
                // If we have a pending session, emit session start event and set up context for reminders
                if (hasPendingSession && pendingSessionId) {
                    // Use draftData directly since currentDraft might not be in scope yet
                    const totalDurationMinutes = draftData?.steps?.reduce((total, step) => {
                        const timerInfo = parseTimerInfo(step.timer_info);
                        const durationInSeconds = timerInfo?.duration || step.duration_sec || 0;
                        return total + durationInSeconds;
                    }, 0) / 60 || 0;
                    
                    
                    const contextForReminders = {
                        sessionId: pendingSessionId,
                        userId: user?.uid,
                        recipeId: isRecipe ? recipeId : null,
                        draftId: isRecipe ? null : draftId,
                        currentStep: 0,
                        totalSteps: currentDraft?.steps?.length || 0,
                        totalDurationMinutes: Math.ceil(totalDurationMinutes),
                        phase: 'active'
                    };
                    
                    liveSessionEvents.setSessionContext(contextForReminders);
                    if (chefRemindersEnabled) {
                        chefReminders.setSessionContext(contextForReminders);
                    }
                    
                    // Emit session start event
                    liveSessionEvents.emitSessionStart({
                        isNewSession: true,
                        sessionData: { id: pendingSessionId }
                    });
                    
                    // Initialize session report
                    sessionReport.initializeSession(pendingSessionId);
                }
            }
            
            // Only initialize prerequisites and first step timer for NEW sessions
            // Skip this for resumed/incomplete sessions (they already have their state restored)
            if (!incompleteSession) {
                // Set showPrerequisites based on whether prerequisites actually exist
                const prerequisites = getPrerequisitesForDisplay();
                const hasPrerequisites = prerequisites && prerequisites.length > 0;
                setShowPrerequisites(hasPrerequisites);
                
                // Start timer for first step if it has timer info and we're not showing prerequisites
                if (!hasPrerequisites) {
                    const steps = getStepsForDisplay();
                    const firstStep = steps?.[0];
                    if (firstStep) {
                        // Check for timer info in the step
                        const timerInfo = parseTimerInfo(firstStep.timer_info);
                        const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                        const duration = hasTimer ? timerInfo.duration : (firstStep.duration_sec || 0);
                        
                        if (duration > 0) {
                            setTimeRemaining(duration);
                            setIsTimerActive(true); // Always auto-start
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching recipe or draft:', err);
            setError('Failed to load cooking session. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch user agent settings
    const fetchAgentSettings = async () => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/users/agent`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const settings = await response.json();
                setAgentSettings(settings);
                setAgentSettingsLoaded(true);
                // console.log('Agent settings loaded:', settings);
            }
        } catch (error) {
            console.error('Error fetching agent settings:', error);
            // Set loaded to true even on error so the modal can show with defaults
            setAgentSettingsLoaded(true);
        }
    };

    // Update session with current step
    const updateSessionStep = async (stepIndex) => {
        if (!sessionId) return;
        
        try {
            const token = await user.getIdToken();
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            await fetch(`${API_BASE_URL}/sessions/${sessionId}/step`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_step: stepIndex
                })
            });
        } catch (error) {
            console.error('Error updating session step:', error);
        }
    };

    const nextStep = useCallback(async () => {
        if (!draft?.steps) return;
        
        // Stop current narration and alarm when moving to next step
        stopSpeech();
        stopRepeatingAlarm();
        
        // If showing prerequisites, move to first step
        if (showPrerequisites) {
            setShowPrerequisites(false);
            setCurrentStepIndex(0);
            await updateSessionStep(0);
            
            // Emit phase change event
            liveSessionEvents.emitPhaseChange('active', {
                fromPrerequisites: true,
                stepIndex: 0
            });
            
            // Setup timers for first step from timing_segments
            const steps = getStepsForDisplay();
            const firstStep = steps[0];
            if (firstStep) {
                // Start timers from timing_segments (new system)
                startTimersForStep(firstStep, 0);
                
                // Legacy timer support (fallback to timer_info if no timing_segments)
                const timingSegments = parseTimingSegments(firstStep.timing_segments);
                if (timingSegments.length === 0) {
                    const timerInfo = parseTimerInfo(firstStep.timer_info);
                    const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                    const duration = hasTimer ? timerInfo.duration : (firstStep.duration_sec || 0);
                    
                    if (duration > 0) {
                        setTimeRemaining(duration);
                        setIsTimerActive(true); // Always auto-start
                    } else {
                        setTimeRemaining(0);
                        setIsTimerActive(false);
                    }
                }
            }
            return;
        }
        
        const steps = getStepsForDisplay();
        if (currentStepIndex < steps.length - 1) {
            const nextIndex = currentStepIndex + 1;
            setCurrentStepIndex(nextIndex);
            await updateSessionStep(nextIndex);
            
            // Emit phase change event for step transition
            liveSessionEvents.emitPhaseChange('active', {
                stepTransition: true,
                fromStep: currentStepIndex,
                toStep: nextIndex
            });
            
            // Setup timers for next step from timing_segments
            const nextStep = draft.steps[nextIndex];
            if (nextStep) {
                // Start timers from timing_segments (new system)
                startTimersForStep(nextStep, nextIndex);
                
                // Legacy timer support (fallback to timer_info if no timing_segments)
                const timingSegments = parseTimingSegments(nextStep.timing_segments);
                if (timingSegments.length === 0) {
                    const timerInfo = parseTimerInfo(nextStep.timer_info);
                    const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                    const duration = hasTimer ? timerInfo.duration : (nextStep.duration_sec || 0);
                    
                    if (duration > 0) {
                        setTimeRemaining(duration);
                        setIsTimerActive(true); // Always auto-start
                    } else {
                        setTimeRemaining(0);
                        setIsTimerActive(false);
                    }
                }
            }
        } else {
            // Last step - show reflection modal instead of completing session
            // CRITICAL: Stop voice service BEFORE speaking final message
            setVoiceEnabled(false); // Disable voice at state level
            voiceService.stopListening();
            voiceService.destroy();
            updateVoiceDisabledState(true); // Disable voice completely
            
            // Speak AFTER voice is stopped (only if narration is enabled)
            if (narrateEnabled) {
                speak('Cooking session complete! Great job!');
            }
            
            // Show reflection modal
            setShowReflectionModal(true);
        }
    }, [currentStepIndex, draft, sessionId, user, showPrerequisites, narrateEnabled]);

    const previousStep = useCallback(() => {
        // If on first step, go back to prerequisites
        if (currentStepIndex === 0 && !showPrerequisites) {
            setShowPrerequisites(true);
            setCurrentStepIndex(0);
            setTimeRemaining(0);
            setIsTimerActive(false);
            return;
        }
        
        if (currentStepIndex > 0) {
            // Stop current narration and alarm when moving to previous step
            stopSpeech();
            audioManager.stopAllAudio();
            
            const prevIndex = currentStepIndex - 1;
            setCurrentStepIndex(prevIndex);
            
            // Setup timers for previous step from timing_segments
            const prevStep = draft.steps[prevIndex];
            if (prevStep) {
                // Start timers from timing_segments (new system)
                startTimersForStep(prevStep, prevIndex);
                
                // Legacy timer support (fallback to timer_info if no timing_segments)
                const timingSegments = parseTimingSegments(prevStep.timing_segments);
                if (timingSegments.length === 0) {
                    const timerInfo = parseTimerInfo(prevStep.timer_info);
                    const hasTimer = timerInfo?.enabled && timerInfo?.duration > 0;
                    const duration = hasTimer ? timerInfo.duration : (prevStep.duration_sec || 0);
                    
                    if (duration > 0) {
                        setTimeRemaining(duration);
                        setIsTimerActive(true); // Auto-start timer when going back
                    } else {
                        setTimeRemaining(0);
                        setIsTimerActive(false);
                    }
                }
            }
        }
    }, [currentStepIndex, draft, showPrerequisites]);

    const startTimer = () => {
        if (timeRemaining > 0) {
            setIsTimerActive(true);
        }
    };

    const pauseLegacyTimer = () => {
        setIsTimerActive(false);
    };

    // Audio Management System - Complete Overhaul with Volume Control
    const audioManager = {
        // Stop all audio and clear all references
        stopAllAudio: () => {
            const manager = audioManagerRef.current;
            // Stop oscillator only
            if (manager.oscillator) {
                try {
                    manager.oscillator.stop();
                } catch (error) {
                    // console.log('Oscillator stop error:', error);
                }
                manager.oscillator = null;
            }
            // Do NOT close audio context here
            // Only close on unmount
            if (manager.customAudio) {
                try {
                    manager.customAudio.pause();
                    manager.customAudio.currentTime = 0;
                } catch (error) {
                    // console.log('Custom audio stop error:', error);
                }
                manager.customAudio = null;
            }
            manager.isAlarmActive = false;
            manager.isPlaying = false;
            setTimerAlarmActive(false);
        },
        // Play a sound with proper cleanup and volume control
        playSound: (soundType, duration = 0.5) => {
            const manager = audioManagerRef.current;
            // Get current volume from user preferences
            const volume = userPreferences.alarm_volume || 0.5;
            manager.volume = volume;
            // Stop any current oscillator first
            if (manager.oscillator) {
                try {
                    manager.oscillator.stop();
                } catch (error) {
                    // console.log('Oscillator stop error:', error);
                }
                manager.oscillator = null;
            }
            // Reuse or create AudioContext
            let audioContext = manager.audioContext;
            if (!audioContext || audioContext.state === 'closed') {
                audioContext = new window.AudioContext();
                manager.audioContext = audioContext;
            }
            if (audioContext.state === 'suspended') {
                audioContext.resume();
            }
            try {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                manager.oscillator = oscillator;
                manager.isPlaying = true;
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                const baseVolume = volume;
                switch (soundType) {
                    case 'default':
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
                        gainNode.gain.setValueAtTime(0.3 * baseVolume, audioContext.currentTime);
                        break;
                    case 'bell':
                        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
                        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
                        gainNode.gain.setValueAtTime(0.2 * baseVolume, audioContext.currentTime);
                        break;
                    case 'chime':
                        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(554, audioContext.currentTime + 0.2);
                        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.4);
                        gainNode.gain.setValueAtTime(0.15 * baseVolume, audioContext.currentTime);
                        break;
                    case 'beep':
                        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
                        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.4);
                        gainNode.gain.setValueAtTime(0.25 * baseVolume, audioContext.currentTime);
                        break;
                    default:
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                        gainNode.gain.setValueAtTime(0.3 * baseVolume, audioContext.currentTime);
                }
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + duration);
                setTimeout(() => {
                    if (manager.oscillator === oscillator) {
                        manager.oscillator = null;
                        manager.isPlaying = false;
                    }
                }, duration * 1000 + 100);
            } catch (error) {
                console.error('Audio playback error:', error);
                if (manager.oscillator) {
                    try {
                        manager.oscillator.stop();
                    } catch (e) {
                        // console.log('Error stopping oscillator:', e);
                    }
                    manager.oscillator = null;
                }
                manager.isPlaying = false;
            }
        },
        // Start repeating alarm
        startRepeatingAlarm: () => {
            const manager = audioManagerRef.current;
            if (!userPreferences.alarm_enabled || manager.isAlarmActive) return;
            manager.isAlarmActive = true;
            setTimerAlarmActive(true);
            const soundType = userPreferences.alarm_sound || 'default';
            audioManager.playSound(soundType);
            manager.alarmInterval = setInterval(() => {
                if (manager.isAlarmActive) {
                    audioManager.playSound(soundType);
                }
            }, 2000);
        },
        // Stop repeating alarm
        stopRepeatingAlarm: () => {
            audioManager.stopAllAudio();
        }
    };

    const startRepeatingAlarm = () => {
        audioManager.startRepeatingAlarm();
    };

    const stopRepeatingAlarm = () => {
        audioManager.stopRepeatingAlarm();
    };

    const pauseSession = async () => {
        try {
            if (!sessionId || sessionComplete) return;
            
            // Stop narration and alarm when pausing session
            stopSpeech();
            audioManager.stopAllAudio();
            
            // Calculate elapsed time since last resume and add to total
            if (lastResumeTime) {
                const elapsedSinceResume = Math.floor((Date.now() - lastResumeTime) / 1000);
                setTotalElapsedTime(prev => prev + elapsedSinceResume);
                setLastResumeTime(null);
            }
            
            const token = await user.getIdToken();
            await pauseCookingSession(sessionId, currentStepIndex, timeRemaining, token);
            
            setIsPaused(true);
            setIsTimerActive(false); // Also pause the timer
            // console.log('Session paused successfully');
        } catch (error) {
            console.error('Error pausing session:', error);
            alert('Failed to pause session. Please try again.');
        }
    };

    const resumeSession = async () => {
        try {
            if (!sessionId || sessionComplete) return;
            
            // Adjust session start time to account for the pause duration
            // This ensures the elapsed time calculation remains accurate
            const pauseDuration = Math.floor((Date.now() - sessionStartTime) / 1000) - totalElapsedTime;
            const newSessionStartTime = Date.now() - (totalElapsedTime * 1000);
            setSessionStartTime(newSessionStartTime);
            
            // Set resume time for tracking
            setLastResumeTime(Date.now());
            
            const token = await user.getIdToken();
            // The resume endpoint doesn't need additional data - it just unpauses
            await resumeCookingSession(sessionId, token);
            
            setIsPaused(false);
            // Don't automatically start timer - let user start it manually
            // console.log('Session resumed successfully');
        } catch (error) {
            console.error('Error resuming session:', error);
            alert('Failed to resume session. Please try again.');
        }
    };

    const handleNavigation = (path) => {
        // Stop any ongoing narration and alarm when navigating
        stopSpeech();
        audioManager.stopAllAudio();
        
        // Force stop voice service immediately
        voiceService.stopListening({ disableAutoRestart: true });
        voiceService.destroy();
        
        if (!sessionComplete && !isPaused && sessionId) {
            // Block navigation and show modal
            setPendingNavigation(path);
            setShowNavigationModal(true);
        } else {
            // Allow navigation if session is complete or already paused
            navigate(path);
        }
    };

    const confirmStopSession = async () => {
        try {
            // Stop narration immediately when stopping session
            stopSpeech();
            audioManager.stopAllAudio();
            
            // Force stop voice service
            voiceService.stopListening({ disableAutoRestart: true });
            voiceService.destroy();
            
            // Delete the session and all related data
            if (sessionId && user) {
                const token = await user.getIdToken();
                await deleteCookingSession(sessionId, token);
                
                // Clear session state
                setSessionId(null);
                setSessionComplete(false);
                setCurrentStepIndex(0);
                setTimeRemaining(0);
                setIsTimerActive(false);
                setIsPaused(false);
                
                // Clear session report and reminders
                sessionReport.clearReport();
                chefReminders.cleanup();
            }
            
            setShowNavigationModal(false);
            // Navigate to the pending path after deletion
            if (pendingNavigation) {
                navigate(pendingNavigation);
            }
            setPendingNavigation(null);
        } catch (error) {
            console.error('❌ Error deleting session:', error);
            // Still navigate away even if deletion fails
            setShowNavigationModal(false);
            if (pendingNavigation) {
                navigate(pendingNavigation);
            }
            setPendingNavigation(null);
        }
    };

    const confirmPauseAndLeave = async () => {
        try {
            await pauseSession(); // pauseSession already calls stopSpeech and audioManager.stopAllAudio()
            
            // Force stop voice service
            voiceService.stopListening({ disableAutoRestart: true });
            voiceService.destroy();
            
            setShowNavigationModal(false);
            // Navigate to the pending path after pausing
            if (pendingNavigation) {
                navigate(pendingNavigation);
            }
            setPendingNavigation(null);
        } catch (error) {
            // Error already handled in pauseSession
            setShowNavigationModal(false);
            setPendingNavigation(null);
        }
    };

    const cancelNavigation = () => {
        setShowNavigationModal(false);
        setPendingNavigation(null);
    };

    const clearVoiceRestartTimeout = () => {
        if (voiceRestartTimeoutRef.current) {
            clearTimeout(voiceRestartTimeoutRef.current);
            voiceRestartTimeoutRef.current = null;
        }
    };

    const updateVoiceDisabledState = (value) => {
        if (voiceDisabledRef.current === value) {
            return;
        }

        voiceDisabledRef.current = value;
        setVoiceDisabled(value);
    };

    const markSpeechEnded = () => {
        const now = Date.now();
        lastSpeechEndTimeRef.current = now;
        setLastSpeechEndTime(now);
    };

    const setWaitingForQuestionState = (value) => {
        if (isWaitingForQuestionRef.current === value) {
            return;
        }

        isWaitingForQuestionRef.current = value;
        setIsWaitingForQuestion(value);
    };

    const queueVoiceRestart = (delayMs = VOICE_RESTART_DELAY_MS) => {
        clearVoiceRestartTimeout();

        if (!voiceEnabledRef.current || !voiceConsentGivenRef.current) {
            // console.log('Voice companion disabled or consent missing - skipping voice restart');
            return;
        }

        voiceRestartTimeoutRef.current = setTimeout(() => {
            voiceRestartTimeoutRef.current = null;

            if (!voiceEnabledRef.current || !voiceConsentGivenRef.current) {
                // console.log('Voice companion disabled or consent missing - skipping voice restart');
                return;
            }

            if (isSpeakingRef.current) {
                // console.log('AI still speaking - delaying voice recognition restart');
                queueVoiceRestart(Math.max(delayMs, VOICE_RESTART_DELAY_MS) + 600);
                return;
            }

            // console.log('Re-enabling voice recognition after AI speech');
            updateVoiceDisabledState(false);
            voiceService.startListening();
        }, delayMs);
    };

    const stopSpeech = ({ resumeListening = true } = {}) => {
        // Stop any existing speech/audio
        if (speechRef.current) {
            // Handle Audio objects (from OpenAI TTS)
            if (speechRef.current instanceof Audio) {
                speechRef.current.pause();
                speechRef.current.currentTime = 0; // Reset to beginning
                if (speechRef.current.src && speechRef.current.src.startsWith('blob:')) {
                    URL.revokeObjectURL(speechRef.current.src);
                }
            }
            // Handle SpeechSynthesisUtterance objects (from browser fallback)
            else if (speechRef.current instanceof SpeechSynthesisUtterance) {
                window.speechSynthesis.cancel();
            }
            speechRef.current = null;
        }
        
        // Always cancel any ongoing browser speech synthesis
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
        
        // Clear speech queue and reset speaking state
        speechQueueRef.current = [];
        isSpeakingRef.current = false;
        setIsSpeaking(false);

        clearVoiceRestartTimeout();

        // Re-enable voice recognition if it was disabled
        if (resumeListening) {
            updateVoiceDisabledState(false);
            if (voiceEnabled && voiceConsentGiven) {
                queueVoiceRestart();
            }
        } else {
            voiceService.stopListening({ disableAutoRestart: true });
        }
    };

    const speak = async (text) => {
        // If already speaking, queue this text
        if (isSpeakingRef.current) {
            speechQueueRef.current.push(text);
            return;
        }

        // Stop any existing speech first
        stopSpeech({ resumeListening: false });
        isSpeakingRef.current = true;
        setIsSpeaking(true);
        
        // AGGRESSIVE: Completely disable voice recognition while AI is speaking
        updateVoiceDisabledState(true);
        voiceService.stopListening();

        // console.log('AI starting to speak - voice recognition disabled');

        try {
            // 🔐 Firebase ID token lets the back-end verify the user
            const token = await user.getIdToken();
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const res = await fetch(`${API_BASE_URL}/audio/speech`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ text })
            });

            if (!res.ok) throw new Error('TTS request failed');

            // Turn the MP3 bytes into something we can play
            const arrayBuffer = await res.arrayBuffer();
            const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
            const url = URL.createObjectURL(blob);

            const audio = new Audio(url);
            speechRef.current = audio;
            
            // Set up event handlers for audio completion
            audio.onended = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                markSpeechEnded();

                // console.log('AI finished speaking - returning to wake word listening');

                queueVoiceRestart();

                // Process next item in queue
                if (speechQueueRef.current.length > 0) {
                    const nextText = speechQueueRef.current.shift();
                    speak(nextText);
                }
            };

            audio.onerror = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                markSpeechEnded();

                // console.log('AI speech error - returning to wake word listening');

                queueVoiceRestart();

                // Process next item in queue even on error
                if (speechQueueRef.current.length > 0) {
                    const nextText = speechQueueRef.current.shift();
                    speak(nextText);
                }
            };
            
            audio.play();
        } catch (err) {
            console.error('TTS failed – falling back to browser voice', err);

            // Fallback to the old SpeechSynthesis so the user still hears something
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.rate = 0.9;
                utterance.pitch = 1;
                utterance.volume = 0.8;
                speechRef.current = utterance;
                
                // Set up event handlers for speech synthesis completion
                utterance.onend = () => {
                    isSpeakingRef.current = false;
                    setIsSpeaking(false);
                    markSpeechEnded();

                    // console.log('AI finished speaking (browser fallback) - returning to wake word listening');

                    queueVoiceRestart();

                    // Process next item in queue
                    if (speechQueueRef.current.length > 0) {
                        const nextText = speechQueueRef.current.shift();
                        speak(nextText);
                    }
                };

                utterance.onerror = () => {
                    isSpeakingRef.current = false;
                    setIsSpeaking(false);
                    markSpeechEnded();

                    // console.log('AI speech error (browser fallback) - returning to wake word listening');

                    queueVoiceRestart();

                    // Process next item in queue even on error
                    if (speechQueueRef.current.length > 0) {
                        const nextText = speechQueueRef.current.shift();
                        speak(nextText);
                    }
                };
                
                window.speechSynthesis.speak(utterance);
            } else {
                isSpeakingRef.current = false;
            }
        }
    };

    const playNotificationSound = () => {
        // This function is now handled by the audioManager
        // It's kept for compatibility but delegates to the new system
        if (userPreferences.alarm_enabled) {
            const soundType = userPreferences.alarm_sound || 'default';
            audioManager.playSound(soundType);
        }
    };

    // Legacy audio functions removed - now handled by audioManager





    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };


    // Toggle ingredients popup
    const toggleIngredientsPopup = () => {
        setShowIngredientsPopup(prev => !prev);
    };

    // Toggle ingredient checked state
    const toggleIngredientChecked = (ingredientKey) => {
        setCheckedIngredients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ingredientKey)) {
                newSet.delete(ingredientKey);
            } else {
                newSet.add(ingredientKey);
            }
            return newSet;
        });
    };

    // Voice command handlers
    const handleVoiceCommand = (command) => {
        const now = Date.now();
        const timeSinceLastCommand = now - lastCommandTime;
        
        // Debounce: ignore commands that come within 10 seconds of the last command
        if (timeSinceLastCommand < 10000) {
            // console.log('Command ignored due to debouncing:', command);
            return;
        }
        
        // Additional check: ignore if AI is still speaking
        if (isSpeaking) {
            // console.log('Command ignored - AI is still speaking:', command);
            return;
        }
        
        // console.log('Processing voice command:', command);
        setLastCommandTime(now);
        
        switch (command.type) {
            case 'navigation':
                handleNavigationCommand(command);
                break;
            case 'timer':
                handleTimerCommand(command);
                break;
            case 'ingredients':
                handleIngredientCommand(command);
                break;
            case 'question':
                handleQuestionCommand(command.parameters.question);
                break;
            default:
                // console.log('Unknown voice command type:', command.type);
        }
    };

    const handleNavigationCommand = (command) => {
        switch (command.action) {
            case 'next_step':
                nextStep();
                // Don't speak here - nextStep() will handle step announcement
                break;
            case 'previous_step':
                previousStep();
                // Don't speak here - previousStep() will handle step announcement
                break;
            case 'go_to_step':
                // Handle direct step navigation from voice command
                if (command.actionData && typeof command.actionData.stepIndex === 'number') {
                    const targetStep = command.actionData.stepIndex;
                    const steps = getStepsForDisplay();
                    if (targetStep >= 0 && targetStep < steps.length && targetStep !== currentStepIndex) {
                        setCurrentStepIndex(targetStep);
                        updateSessionStep(targetStep);
                        const step = steps[targetStep];
                        const stepTitle = step?.title || `Step ${targetStep + 1}`;
                        const stepBody = step?.body || '';
                        const announcement = stepBody 
                            ? `${stepTitle}. ${stepBody}` 
                            : stepTitle;
                        speak(announcement);
                    }
                }
                break;
            case 'start_cooking':
                if (showPrerequisites) {
                    nextStep();
                    speak('Starting cooking session');
                }
                break;
            case 'finish_cooking':
                nextStep(); // This will trigger session completion
                speak('Finishing cooking session');
                break;
        }
    };

    const handleTimerCommand = (command) => {
        switch (command.action) {
            case 'start_timer':
                if (command.parameters.duration > 0) {
                    setTimeRemaining(command.parameters.duration);
                    setIsTimerActive(true);
                    speak(`Starting timer for ${command.parameters.duration} seconds`);
                } else {
                    speak('Please specify a duration for the timer');
                }
                break;
            case 'stop_timer':
                setIsTimerActive(false);
                setTimeRemaining(0);
                speak('Timer stopped');
                break;
        }
    };

    const handleIngredientCommand = (command) => {
        const { amount, unit, ingredient } = command.parameters;
        const ingredientText = `${amount} ${unit} of ${ingredient}`;
        
        // Find matching ingredient in the list
        const ingredients = getIngredientsForDisplay();
        const matchingIndex = ingredients.findIndex(ing => 
            ing.toLowerCase().includes(ingredient.toLowerCase())
        );
        
        if (matchingIndex !== -1) {
            const ingredientKey = `ingredient_${matchingIndex}`;
            toggleIngredientChecked(ingredientKey);
            speak(`Marked ${ingredientText} as used`);
        } else {
            speak(`I don't see ${ingredient} in your ingredient list`);
        }
    };

    const handleQuestionCommand = async (questionText) => {
        // console.log('Processing question:', questionText);
        
        // Show loading state
        setIsAIThinking(true);
        
        try {
            // Call the AI chat endpoint
            const token = await user.getIdToken();
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/audio/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    text: questionText,
                    sessionId: sessionId,
                    currentStep: currentStepIndex // Send current step from frontend to ensure accuracy
                })
            });

            if (!response.ok) {
                throw new Error(`AI chat request failed with status ${response.status}`);
            }

            const aiResponse = await response.json();
            // console.log('AI Response received:', aiResponse);

            // CRITICAL: Double-check that questions don't have actions
            // If the question contains question words, strip the action
            const questionLower = questionText.toLowerCase();
            const isQuestion = questionLower.includes('what') || 
                              questionLower.includes('tell me') || 
                              questionLower.includes('show me') || 
                              questionLower.includes('explain') ||
                              questionLower.includes('how') ||
                              questionLower.startsWith('what\'s') ||
                              questionLower.startsWith('what is');
            
            if (isQuestion && aiResponse.action === 'step') {
                aiResponse.action = null;
                aiResponse.actionData = null;
            }

            // Handle any actions from the AI (but only if it's actually a command, not a question)
            // If the user asked a question like "what's the next step?", the AI should NOT return an action
            if (aiResponse.action && aiResponse.action !== null && !isQuestion) {
                await handleAIAction(aiResponse.action, aiResponse.actionData);
            }

            // Speak the AI response
            speak(aiResponse.speak || aiResponse.text);
            
        } catch (error) {
            console.error('Error processing AI question:', error);
            
            // Simple fallback for any error
            speak("I'm having trouble connecting to the AI service right now. Please try again in a moment.");
            
            // Ensure voice recognition is re-enabled after error
            setTimeout(() => {
                if (voiceEnabled && voiceConsentGiven) {
                    updateVoiceDisabledState(false);
                    voiceService.startListening();
                }
            }, 1000);
        } finally {
            // Hide loading state
            setIsAIThinking(false);
        }
    };


    // Handle AI actions (timer, conversion, step navigation, etc.)
    const handleAIAction = async (action, actionData) => {
        // console.log('Handling AI action:', action, actionData);
        
        switch (action) {
            case 'timer':
                if (actionData && actionData.duration) {
                    setTimeRemaining(actionData.duration);
                    setIsTimerActive(true);
                    // console.log(`AI started timer: ${actionData.duration}s - ${actionData.description || 'AI timer'}`);
                }
                break;
                
            case 'conversion':
                if (actionData && actionData.from && actionData.to) {
                    // console.log(`AI provided conversion: ${actionData.from} = ${actionData.to}`);
                    // Conversion info is already in the spoken response
                }
                break;
                
            case 'step':
                if (actionData && typeof actionData.stepIndex === 'number') {
                    const targetStep = actionData.stepIndex;
                    const steps = getStepsForDisplay();
                    
                    // Validate stepIndex is within bounds
                    if (targetStep < 0 || targetStep >= steps.length) {
                        console.warn(`Invalid stepIndex from AI: ${targetStep}, valid range: 0-${steps.length - 1}`);
                        speak(`I can't go to step ${targetStep + 1}. There are only ${steps.length} steps.`);
                        break;
                    }
                    
                    // Only navigate if we're actually moving to a different step
                    if (targetStep !== currentStepIndex) {
                        // Validate we're only moving one step at a time (unless explicitly going to a specific step number)
                        const stepDifference = Math.abs(targetStep - currentStepIndex);
                        if (stepDifference > 1) {
                            console.warn(`AI tried to move ${stepDifference} steps. Limiting to 1 step.`);
                            // If moving forward, only move 1 step forward
                            const correctedStep = targetStep > currentStepIndex 
                                ? currentStepIndex + 1 
                                : currentStepIndex - 1;
                            
                            if (correctedStep >= 0 && correctedStep < steps.length) {
                                setCurrentStepIndex(correctedStep);
                                await updateSessionStep(correctedStep);
                                
                                const step = steps[correctedStep];
                                const stepTitle = step?.title || `Step ${correctedStep + 1}`;
                                const stepBody = step?.body || '';
                                const announcement = stepBody 
                                    ? `${stepTitle}. ${stepBody}` 
                                    : stepTitle;
                                speak(announcement);
                            }
                        } else {
                            // Normal single-step navigation
                            setCurrentStepIndex(targetStep);
                            await updateSessionStep(targetStep);
                            
                            // Have Chef announce the step
                            const step = steps[targetStep];
                            const stepTitle = step?.title || `Step ${targetStep + 1}`;
                            const stepBody = step?.body || '';
                            const announcement = stepBody 
                                ? `${stepTitle}. ${stepBody}` 
                                : stepTitle;
                            speak(announcement);
                        }
                        
                        // console.log(`AI navigated to step: ${targetStep + 1}`);
                    } else {
                        // Already on this step, just confirm
                        const step = steps[targetStep];
                        const stepTitle = step?.title || `Step ${targetStep + 1}`;
                        speak(`You're already on ${stepTitle}`);
                    }
                }
                break;
                
            case 'ingredient':
                if (actionData && actionData.ingredient) {
                    // Find and mark ingredient as used
                    const ingredients = getIngredientsForDisplay();
                    const matchingIndex = ingredients.findIndex(ing => 
                        ing.toLowerCase().includes(actionData.ingredient.toLowerCase())
                    );
                    
                    if (matchingIndex !== -1) {
                        const ingredientKey = `ingredient_${matchingIndex}`;
                        toggleIngredientChecked(ingredientKey);
                        // console.log(`AI marked ingredient as used: ${actionData.ingredient}`);
                    }
                }
                break;
                
            default:
                // console.log('Unknown AI action:', action);
        }
    };

    // Voice consent handlers - No longer needed, handled by MicrophonePermissionModal
    // These are kept for backwards compatibility but should not be called
    const handleVoiceConsentAccept = () => {
        // This should not be called anymore, but kept for safety
        setVoiceConsentGiven(true);
        setVoiceEnabled(true);
    };

    const handleVoiceConsentDecline = () => {
        // This should not be called anymore, but kept for safety
        setVoiceConsentGiven(true);
        setVoiceEnabled(false);
        voiceService.disableVoiceRecognition();
    };

    const handleVoiceConsentClose = () => {
        // This should not be called anymore, but kept for safety
        // No-op
    };

    const toggleVoiceEnabled = () => {
        if (!voiceConsentGiven) {
            setShowVoiceConsent(true);
        } else {
            const newVoiceEnabled = !voiceEnabled;
            setVoiceEnabled(newVoiceEnabled);
            
            if (newVoiceEnabled) {
                // Re-enable voice recognition
                voiceService.enableAutoRestart();
                setTimeout(() => {
                    const success = voiceService.startListening();
                    if (success) {
                        speak(`Voice companion enabled. Say "${agentSettings.agent_trigger}" to give commands.`);
                    }
                }, 100);
            } else {
                // Explicitly disable voice recognition
                voiceService.disableVoiceRecognition();
                speak('Voice companion disabled.');
            }
        }
    };

    // Get ingredients for display
    const getIngredientsForDisplay = () => {
        // Priority 1: Use session ingredients if available (custom ingredients from modal)
        if (sessionId && sessionData?.recipe_ingredients && Array.isArray(sessionData.recipe_ingredients) && sessionData.recipe_ingredients.length > 0) {
            return sessionData.recipe_ingredients;
        }
        
        // Priority 2: Use actual AI-generated ingredients (preferred)
        if (draft?.ingredients && Array.isArray(draft.ingredients) && draft.ingredients.length > 0) {
            return draft.ingredients;
        }
        
        // Priority 3: Try to extract from pantry field for old drafts 
        if (draft?.slots?.pantry && typeof draft.slots.pantry === 'string') {
            return draft.slots.pantry
                .split(',')
                .map(item => item.trim())
                .filter(item => item.length > 0 && item.length < 50);
        }
        
        // Priority 4: Filter out non-ingredient slots for legacy drafts
        return Object.entries(draft?.slots || {})
            .filter(([key, value]) => 
                !['goal', 'timeMins', 'pantry', 'userGoal', 'timeBudget'].includes(key) &&
                typeof value === 'string' && 
                value.length > 0 && 
                value.length < 100
            )
            .map(([key, value]) => {
                const ingredientName = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
                return `${value} ${ingredientName}`;
            });
    };

    // Get steps for display (prefer draft steps which have componentName for multi-component recipes)
    const getStepsForDisplay = () => {
        // Priority 1: Use draft steps (has componentName for multi-component recipes)
        if (draft?.steps && Array.isArray(draft.steps) && draft.steps.length > 0) {
            return draft.steps;
        }
        
        // Priority 2: Fall back to session steps if draft steps not available
        if (sessionId && sessionData?.recipe_instructions && Array.isArray(sessionData.recipe_instructions) && sessionData.recipe_instructions.length > 0) {
            return sessionData.recipe_instructions.map((stepText, index) => ({
                title: `Step ${index + 1}`,
                body: stepText,
                duration_sec: 0, // Default duration
                timer_info: JSON.stringify({ enabled: false, duration: 0, description: stepText, ingredients: [] }),
                componentName: null // No component info in session steps fallback
            }));
        }
        
        return [];
    };

    // Get prerequisites for display (use session data if available)
    const getPrerequisitesForDisplay = () => {
        // Priority 1: Use session prerequisites if available (custom prerequisites from modal)
        if (sessionId && sessionData?.recipe_instructions && sessionData.recipe_instructions.prerequisites && Array.isArray(sessionData.recipe_instructions.prerequisites)) {
            return sessionData.recipe_instructions.prerequisites;
        }
        
        // Priority 2: Use draft prerequisites
        if (draft?.prerequisites && Array.isArray(draft.prerequisites) && draft.prerequisites.length > 0) {
            return draft.prerequisites;
        }
        
        return [];
    };

    // Helper function to safely parse timer_info
    const parseTimerInfo = (timerInfo) => {
        if (!timerInfo) return null;
        if (typeof timerInfo === 'object') return timerInfo;
        if (typeof timerInfo === 'string') {
            try {
                return JSON.parse(timerInfo);
            } catch (e) {
                console.warn('Failed to parse timer_info:', e);
                return null;
            }
        }
        return null;
    };

    // Helper function to safely parse timing_segments
    const parseTimingSegments = (timingSegments) => {
        if (!timingSegments) return [];
        if (Array.isArray(timingSegments)) return timingSegments;
        if (typeof timingSegments === 'string') {
            try {
                const parsed = JSON.parse(timingSegments);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.warn('Failed to parse timing_segments:', e);
                return [];
            }
        }
        return [];
    };

    // Timer management functions
    const addTimer = (title, duration, stepIndex = null, kind = null) => {
        const newTimer = {
            id: `timer_${timerIdCounter.current++}`,
            title,
            duration,
            remaining: duration,
            isActive: true,
            isMuted: false,
            stepIndex,
            kind
        };
        setActiveTimers(prev => [...prev, newTimer]);
        return newTimer.id;
    };

    const removeTimer = (timerId) => {
        setActiveTimers(prev => {
            const timer = prev.find(t => t.id === timerId);
            if (timer && timer.isActive && timer.remaining <= 0) {
                // Stop alarm if this timer was ringing
                stopRepeatingAlarm();
            }
            return prev.filter(t => t.id !== timerId);
        });
    };

    const pauseTimer = (timerId) => {
        setActiveTimers(prev => prev.map(t => 
            t.id === timerId ? { ...t, isActive: false } : t
        ));
    };

    const resumeTimer = (timerId) => {
        setActiveTimers(prev => prev.map(t => 
            t.id === timerId ? { ...t, isActive: true } : t
        ));
    };

    const muteTimer = (timerId) => {
        setActiveTimers(prev => prev.map(t => 
            t.id === timerId ? { ...t, isMuted: true } : t
        ));
    };

    const unmuteTimer = (timerId) => {
        setActiveTimers(prev => prev.map(t => 
            t.id === timerId ? { ...t, isMuted: false } : t
        ));
    };

    // Start timers from timing_segments for a step
    const startTimersForStep = (step, stepIndex) => {
        const timingSegments = parseTimingSegments(step.timing_segments);
        if (timingSegments.length === 0) return;

        // Sort by order to start in correct sequence
        const sortedSegments = [...timingSegments].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Use functional update to check existing timers
        setActiveTimers(prev => {
            // Check if timers for this step already exist (to avoid duplicates)
            const existingTimersForStep = prev.filter(t => t.stepIndex === stepIndex);
            const newTimers = [];
            
            sortedSegments.forEach(segment => {
                // Check if this timer already exists for this step
                const existingTimer = existingTimersForStep.find(t => 
                    t.kind === segment.kind && 
                    t.stepIndex === stepIndex
                );
                
                // Add timer if it doesn't exist (even if duration is 0)
                if (!existingTimer) {
                    const duration = segment.expectedSeconds || 0;
                    const newTimer = {
                        id: `timer_${timerIdCounter.current++}`,
                        title: segment.label || (segment.kind === 'active' ? 'Active time' : 'Inactive time'),
                        duration,
                        remaining: duration,
                        isActive: duration > 0, // Only auto-start if duration > 0
                        isMuted: false,
                        stepIndex,
                        kind: segment.kind
                    };
                    newTimers.push(newTimer);
                }
            });
            
            if (newTimers.length > 0) {
                return [...prev, ...newTimers];
            }
            return prev;
        });
    };

    // Add volume control function
    const updateAlarmVolume = (newVolume) => {
        const clampedVolume = Math.max(0, Math.min(1, newVolume)); // Ensure volume is between 0 and 1
        const newPreferences = {
            ...userPreferences,
            alarm_volume: clampedVolume
        };
        setUserPreferences(newPreferences);
        saveUserPreferences(newPreferences);
        
        // Update the audio manager's volume reference
        audioManagerRef.current.volume = clampedVolume;
    };

    // On unmount, close the AudioContext if it exists and cleanup voice service
    useEffect(() => {
        return () => {
            clearVoiceRestartTimeout();

            if (audioManagerRef.current.audioContext && audioManagerRef.current.audioContext.state !== 'closed') {
                audioManagerRef.current.audioContext.close();
            }

            audioManager.stopAllAudio();
            voiceService.stopListening();
            voiceService.destroy();
        };
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearVoiceRestartTimeout();

            if (questionTimeoutRef.current) {
                clearTimeout(questionTimeoutRef.current);
            }

            stopSpeech({ resumeListening: false });
            audioManager.stopAllAudio();
        };
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0 pb-24">
                <div className="w-full py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2} className="text-neutral-900 font-mono uppercase tracking-wider">Loading cooking session...</Heading>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0 pb-24">
                <div className="w-full py-8">
                    <div className="bg-neutral-0 hairline minimal-padding text-center">
                        <Heading level={2} className="text-neutral-900 font-mono uppercase tracking-wider">Error</Heading>
                        <Text className="text-neutral-900 opacity-60">{error}</Text>
                        <button 
                            onClick={() => navigate('/bases')}
                            className="mt-4 px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            ← Back to Techniques
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (sessionComplete) {
        return null;
    }

    // Toggle function for expanding/collapsing step text
    const toggleStepExpansion = (stepIndex) => {
        setExpandedSteps(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stepIndex)) {
                newSet.delete(stepIndex);
            } else {
                newSet.add(stepIndex);
            }
            return newSet;
        });
    };

    const currentStep = getStepsForDisplay()?.[currentStepIndex];
    const upcomingSteps = getStepsForDisplay()?.slice(currentStepIndex + 1) || [];

    return (
        <div className="min-h-screen bg-neutral-50 px-0 pb-24">
            {/* Live Session Event Debugger */}
            <LiveSessionEventDebugger showInUI={false} />
            
            {/* Chef Reminders Debugger */}
            <ChefRemindersDebugger showInUI={false} />
            
            {/* Chef Reminder Modal */}
            {/* Paused Session Modal */}
            {showPausedModal && pausedSession && (
                <PausedSessionModal
                    session={pausedSession}
                    onResume={handlePausedResume}
                    onRestart={handlePausedRestart}
                    onCancel={() => {
                        setShowPausedModal(false);
                        setPausedSession(null);
                        // Navigate away since they don't want to resume or restart
                        navigate('/');
                    }}
                />
            )}

            {/* Session Resume Modal */}
            {showResumeModal && pausedSession && (
                <SessionResumeModal
                    session={pausedSession}
                    onResume={handleResumeSession}
                    onRestart={handleRestartSession}
                    onCancel={() => {
                        setShowResumeModal(false);
                        setPausedSession(null);
                        // Navigate away since they don't want to resume or restart
                        navigate('/');
                    }}
                />
            )}

            <UnifiedChefReminderModal
                isOpen={showReminderModal}
                onClose={() => setShowReminderModal(false)}
                reminder={currentReminder}
                onPhotoTaken={handlePhotoTaken}
                onCheckInResponse={handleCheckInResponse}
                onDismiss={handleReminderDismiss}
                voiceService={voiceService}
                onSpeakText={speak}
                chefReminders={chefReminders}
            />
            
            {/* Chef Summaries Modal */}
            <ChefSummariesModal
                isOpen={showChefSummariesModal}
                onClose={() => {
                    setShowChefSummariesModal(false);
                    // Show posting modal instead of navigating to reflection
                    setShowPostingModal(true);
                }}
                onComplete={handleChefSummariesComplete}
                sessionId={sessionId}
                user={user}
            />
            
            {/* Reflection Modal */}
            <ReflectionModal
                isOpen={showReflectionModal}
                onClose={() => setShowReflectionModal(false)}
                onSkip={handleReflectionSkip}
                onFinishNoPost={handleReflectionFinishNoPost}
                onPost={handleReflectionPost}
                steps={getStepsForDisplay()}
                sessionId={sessionId}
                user={user}
                recipeId={isRecipe ? recipeId : null}
                isRecipe={isRecipe}
            />
            
            {/* Posting Modal */}
            <PostingModal
                isOpen={showPostingModal}
                onClose={() => setShowPostingModal(false)}
                onSubmit={handlePostingComplete}
                sessionId={sessionId}
                sessionReport={sessionReport.getReportData()}
                initialTitle={draft?.title || recipe?.title || ''}
            />
            
            {/* Timer Management Popup */}
            <TimerManagementPopup
                isOpen={showTimerPopup}
                onClose={() => setShowTimerPopup(false)}
                activeTimers={activeTimers}
                currentStepIndex={currentStepIndex}
                onAddTimer={addTimer}
                onRemoveTimer={removeTimer}
                onPauseTimer={pauseTimer}
                onResumeTimer={resumeTimer}
                onMuteTimer={muteTimer}
                onUnmuteTimer={unmuteTimer}
                formatTime={formatTime}
            />
            
            {/* Title section removed - buttons will be converted to floating tabs */}

            {/* Main Content Area - New Layout */}
            <div className="pt-4 pb-4">
                {/* Current Step or Prerequisites */}
                <div className="bg-neutral-0 hairline px-4 pt-6 pb-6 mb-4">
                    {showPrerequisites && getPrerequisitesForDisplay().length > 0 ? (
                        <>
                            <Heading level={1} className="text-neutral-900 text-5xl leading-tight mb-4">
                                Prerequisites
                            </Heading>
                            <div className="text-neutral-900 text-xl leading-relaxed mb-4">
                                {getPrerequisitesForDisplay().map((prereq, index) => (
                                    <div key={index} className="mb-4">
                                        <Text className="text-neutral-900 text-xl leading-relaxed">
                                            {prereq}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : currentStep ? (
                        <>
                            {/* Step Content - Full width on mobile, left side on desktop */}
                            <div className="flex-1 min-w-0 flex flex-col lg:flex-row items-end gap-6">
                                {/* Step Content - Left Side */}
                                <div className="flex-1 min-w-0 flex flex-col w-full lg:w-auto">
                                    {/* Component Name - Gray, Smaller Font - Separate div at top */}
                                    {currentStep.componentName && (
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                                                {currentStep.componentName}
                                            </Text>
                                        </div>
                                    )}
                                    {/* Spacer to push step content to bottom */}
                                    <div className="flex-1 min-h-[120px]"></div>
                                    {/* Step info - At bottom */}
                                    <div>
                                        <Heading level={1} className="text-neutral-900 text-5xl leading-tight mb-4">
                                            {currentStep.title}
                                        </Heading>
                                        <Text className="text-neutral-900 text-xl leading-relaxed">
                                            {currentStep.body}
                                        </Text>
                                    </div>
                                </div>
                                
                                {/* Right Side - Image (hidden on mobile, shown on desktop) */}
                                <div className="hidden lg:block lg:w-96 flex-shrink-0 relative">
                                    {/* Image or Placeholder */}
                                    {recipe?.images && recipe.images.length > 0 ? (
                                        <img 
                                            src={recipe.images[0].url || recipe.images[0]} 
                                            alt={recipe.title || 'Recipe'} 
                                            className="w-full h-auto object-cover hairline"
                                        />
                                    ) : draft?.images && draft.images.length > 0 ? (
                                        <img 
                                            src={draft.images[0].url || draft.images[0]} 
                                            alt={draft.title || 'Recipe'} 
                                            className="w-full h-auto object-cover hairline"
                                        />
                                    ) : (
                                        <div className="w-full h-64 bg-neutral-50 hairline relative flex items-center justify-center" style={{ opacity: 0.5 }}>
                                            <div className="text-center px-4">
                                                <Text className="text-neutral-900 text-sm font-mono uppercase tracking-wider mb-2">
                                                    Add images during cooking
                                                </Text>
                                                <Text className="text-neutral-900 text-xs opacity-60">
                                                    Capture photos as you cook to document your process
                                                </Text>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Mobile Image - Shown only on mobile, below step info */}
                            <div className="lg:hidden w-full mt-6">
                                {/* Image or Placeholder */}
                                {recipe?.images && recipe.images.length > 0 ? (
                                    <img 
                                        src={recipe.images[0].url || recipe.images[0]} 
                                        alt={recipe.title || 'Recipe'} 
                                        className="w-full h-auto object-cover hairline"
                                    />
                                ) : draft?.images && draft.images.length > 0 ? (
                                    <img 
                                        src={draft.images[0].url || draft.images[0]} 
                                        alt={draft.title || 'Recipe'} 
                                        className="w-full h-auto object-cover hairline"
                                    />
                                ) : (
                                    <div className="w-full h-64 bg-neutral-50 hairline relative flex items-center justify-center" style={{ opacity: 0.5 }}>
                                        <div className="text-center px-4">
                                            <Text className="text-neutral-900 text-sm font-mono uppercase tracking-wider mb-2">
                                                Add images during cooking
                                            </Text>
                                            <Text className="text-neutral-900 text-xs opacity-60">
                                                Capture photos as you cook to document your process
                                            </Text>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>

                {/* All Steps - Small, Grayed Out with Full Content */}
                {!showPrerequisites && getStepsForDisplay().length > 0 && (
                    <div className="bg-neutral-0 hairline px-4 py-4">
                        <div className="space-y-3">
                            {getStepsForDisplay().map((step, index) => {
                                const isCurrentStep = index === currentStepIndex;
                                const prevStep = index > 0 ? getStepsForDisplay()[index - 1] : null;
                                const showComponentHeader = step.componentName && step.componentName !== prevStep?.componentName;
                                
                                return (
                                    <div key={index}>
                                        {/* Component Header - Show when component changes */}
                                        {showComponentHeader && (
                                            <div className="mb-2 mt-3 first:mt-0">
                                                <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 font-medium">
                                                    {step.componentName}
                                                </Text>
                                            </div>
                                        )}
                                        <div 
                                            className={`${
                                                isCurrentStep 
                                                    ? 'text-neutral-900 opacity-100' 
                                                    : 'text-neutral-900 opacity-70'
                                            }`}
                                        >
                                            <Text className={`text-xs font-mono uppercase tracking-wider mb-1 ${
                                                isCurrentStep ? 'opacity-100' : 'opacity-70'
                                            }`}>
                                                {step.title}
                                            </Text>
                                            {step.body && (
                                                <Text className={`text-xs leading-relaxed ${
                                                    isCurrentStep ? 'opacity-100' : 'opacity-70'
                                                }`}>
                                                    {step.body}
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>


            {/* Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-neutral-0 hairline-t px-0 py-2">
                <div className="flex justify-between items-center minimal-padding">
                    <button
                        onClick={previousStep}
                        disabled={showPrerequisites && (!draft?.prerequisites || draft.prerequisites.length === 0)}
                        className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider disabled:opacity-30 hover:bg-neutral-50 transition-colors"
                    >
                        ← Previous
                    </button>
                    
                    <div className="flex items-center gap-4">
                        {/* Always-on button */}
                        {wakeLockSupported && (
                            <button
                                onClick={toggleAlwaysOn}
                                className={`w-8 h-8 flex items-center justify-center transition-all ${
                                    alwaysOnEnabled 
                                        ? 'bg-neutral-900 text-neutral-0 hover:opacity-60' 
                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50'
                                }`}
                                title={alwaysOnEnabled ? 'Disable always-on mode' : 'Enable always-on mode'}
                            >
                                {alwaysOnEnabled ? '🔆' : '🔅'}
                            </button>
                        )}
                        
                        <Text className="text-neutral-900 text-sm opacity-60 font-mono uppercase tracking-wider">
                            {showPrerequisites ? 'Press → or Space to start cooking' : 'Press → or Space to continue'}
                        </Text>
                    </div>
                    
                    <button
                        onClick={nextStep}
                        className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                    >
                        {showPrerequisites ? 'Start Cooking →' : (currentStepIndex === draft?.steps?.length - 1 ? 'Finish' : 'Next →')}
                    </button>
                </div>
            </div>


            {/* Kernel Image Slideshow - Bottom Right */}
            {showKernelSlideshow && kernel && (
                <PopupWindow
                    title={kernel.title}
                    images={kernel.images || []}
                    kernelData={kernel}
                    onClose={() => setShowKernelSlideshow(false)}
                    style={{
                        position: 'fixed',
                        bottom: '120px',
                        right: '24px',
                        top: 'auto',
                        left: 'auto'
                    }}
                />
            )}

            {/* Voice Consent Modal - Removed, now handled by MicrophonePermissionModal before session starts */}

            {/* Ingredients Checklist Popup */}
            {showIngredientsPopup && (
                <IngredientsPopupWindow
                    title="Ingredients Checklist"
                    ingredients={getIngredientsForDisplay()}
                    checkedIngredients={checkedIngredients}
                    onToggleIngredient={(ingredientKey) => toggleIngredientChecked(ingredientKey)}
                    onClose={() => setShowIngredientsPopup(false)}
                    style={{
                        position: 'fixed',
                        bottom: '120px',
                        right: '24px',
                        top: 'auto',
                        left: 'auto'
                    }}
                />
            )}

            {/* Floating Tabs - Always visible, even when modals are open */}
            <FloatingIngredientsTab
                onClick={() => {
                    if (showIngredientsPopup) {
                        setShowIngredientsPopup(false);
                    } else {
                        setShowIngredientsPopup(true);
                    }
                }}
                isPopupOpen={showIngredientsPopup}
                onClose={() => setShowIngredientsPopup(false)}
            />
            <FloatingAlarmsTab
                onClick={() => {
                    if (timerAlarmActive) {
                        stopRepeatingAlarm();
                    } else if (showTimerPopup) {
                        setShowTimerPopup(false);
                    } else {
                        setShowTimerPopup(true);
                    }
                }}
                timerAlarmActive={timerAlarmActive}
                activeTimersCount={activeTimers.length}
                timeRemaining={timeRemaining}
                formatTime={formatTime}
                alarmEnabled={userPreferences.alarm_enabled}
                isModalOpen={showTimerPopup}
                onClose={() => setShowTimerPopup(false)}
            />

            {/* Floating Chef */}
            <FloatingChef
                isListening={voiceListening}
                isTriggered={voiceTriggered}
                isEnabled={voiceEnabled}
                onToggle={toggleVoiceEnabled}
                isAIThinking={isAIThinking}
                isSpeaking={isSpeaking}
                agentName={agentSettings.agent_name}
            />


            {/* AI Tip Popup */}
            {showTipPopup && currentTip && (
                <AiTipPopup
                    tip={currentTip}
                    onDismiss={handleTipDismiss}
                    onFeedback={handleTipFeedback}
                    chefPosition={chefPosition}
                />
            )}

            {/* Navigation Confirmation Modal */}
            <Modal
                isOpen={showNavigationModal}
                onClose={cancelNavigation}
                title="Leave Cooking Session?"
            >
                <div className="vspace">
                    <Text>
                        You're currently in an active cooking session. What would you like to do?
                    </Text>
                    <Text className="text-sm text-neutral-600 mt-2">
                        <strong>Pause & Leave:</strong> Save your progress and return later<br/>
                        <strong>Delete & Exit:</strong> Permanently delete this session and all progress
                    </Text>
                    
                    <div className="flex flex-col gap-3 pt-4">
                        <button
                            onClick={confirmPauseAndLeave}
                            className="px-4 py-2 bg-yellow-600 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            ⏸ PAUSE & LEAVE
                        </button>
                        <button
                            onClick={confirmStopSession}
                            className="px-4 py-2 bg-red-600 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            🗑️ DELETE & EXIT
                        </button>
                        <button
                            onClick={cancelNavigation}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            ← STAY HERE
                        </button>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-neutral-900">
                        <Text className="text-neutral-900 opacity-60 text-sm">
                            Paused sessions can be resumed from the Activity page.
                        </Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default LiveSession; 
